/**
 * WidgetPalette Component (Story 5.6 → 5.39 Redesigned)
 *
 * AgencyAnalytics-style split widget palette:
 * - WidgetCategorySidebar: full-height right sidebar with category buttons
 * - WidgetCardsPanel: draggable widget cards for the active category. As of
 *   Story 37.2 these render inside the floating FloatingCategoryPicker shell
 *   (the panel fills it via w-full h-full), not inline in the sidebar.
 *
 * Story 5.39: Migrated from @dnd-kit useDraggable to native HTML5 drag.
 * Palette cards use `draggable="true"` + `onDragStart` with dataTransfer
 * to communicate widget type/size to RGL's `onDrop` handler.
 */

import { useState, useRef, useCallback, useMemo, memo } from "react";
import { createPortal } from "react-dom";
import { MagnifyingGlass, X, CaretRight, CaretLeft, ArrowLineLeft, ArrowLineRight, PaletteIcon } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import { useDraggable } from "@dnd-kit/core";
import { WidgetPreview } from "./WidgetPreview";
import { buildPreviewWidget } from "./widgetPreviewModel";
import { isRollupAllowedWidgetType } from "./rollupPaletteGate";
import type { PaletteDragData } from "../../lib/reportDnd";
import {
  Hash,
  TrendUp,
  ChartBar,
  ChartPieSlice,
  Table,
  TextT,
  Newspaper,
  BookOpen,
  DotsSixVertical,
  Funnel,
  Users,
  TextHOne,
  TextAlignLeft,
  FileText,
  Minus,
  Image,
  Code,
  GridNineIcon, ChartDonutIcon,
  RulerIcon,
  GaugeIcon,
  Flag,
  Envelope,
  Trophy,
  ShoppingCart,
  SquaresFour,
  InstagramLogo,
  FacebookLogo,
  Megaphone,
  TiktokLogo,
  VideoCamera,
  Heart,
  GoogleLogo,
  Lightning,
  YoutubeLogo,
  LinkedinLogo,
  Buildings,
  Handshake,
  GitBranch,
  ArrowsLeftRight,
  MapPin,
  Star,
  LinkSimple,
  WindowsLogo,
  BookmarkSimple,
  FilmStrip,
  DeviceMobile,
  SnapchatLogo,
  ChartBarHorizontal,
  Devices,
  RedditLogo,
  ListBullets,
  Globe,
  // Story 19.17 — Amazon Ads palette icons
  AmazonLogo,
  Storefront,
  Target,
  Stack,
  // Story 20.3 — Ahrefs (SEO) palette icons
  Heartbeat,
  Robot,
  ChartLineUp,
  // Story 20.6 — Semrush (SEO) palette icons
  UsersFour,
  // Story 20.8 — Moz (SEO) palette icons. Anchor = Moz brand mark; Crown =
  // Brand Authority's 2026 flagship signal; ListNumbers = ranked-list
  // (Top Linking Root Domains).
  Anchor,
  Crown,
  ListNumbers,
  // Story 22.2 — CallRail (Call Tracking) palette icons. Phone = canonical
  // call-tracking glyph; PhoneCall + PhoneIncoming = directional variants
  // for KPI / scorecard items.
  Phone,
  PhoneCall,
  PhoneIncoming,
} from "@phosphor-icons/react";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import type { ValidColSpan } from "../../lib/gridUtils";

/** Widget types matching @provenleap/db report-layout-types (local to avoid cross-package import) */
export type WidgetType = "kpi" | "line-chart" | "bar-chart" | "pie-chart" | "data-table" | "text-block" | "top-posts" | "cover-page" | "funnel" | "demographic" | "heading" | "paragraph" | "rich-text" | "spacer-divider" | "image" | "embed" | "custom-metric" | "benchmark" | "goal" | "rollup-table";

/** Preset types — pre-configured combinations of existing widget types. SEPARATE from WidgetType. */
export type PresetType = "email-overview-kpi" | "email-campaign-table" | "email-subscriber-growth" | "email-top-campaigns" | "ecommerce-sales-overview-kpi" | "ecommerce-revenue-trend" | "ecommerce-top-products" | "ecommerce-orders-conversion" | "ecommerce-product-performance" | "ecommerce-profit" | "ecommerce-customer" | "ecommerce-cart-abandonment" | "ecommerce-geo" | "instagram-overview-kpi" | "instagram-reels-table" | "instagram-demographics" | "facebook-pages-overview-kpi" | "facebook-pages-video" | "facebook-pages-reactions" | "tiktok-ads-overview-kpi" | "tiktok-ads-video-funnel" | "tiktok-ads-campaign-table" | "tiktok-organic-overview-kpi" | "tiktok-organic-video-table" | "google-ads-overview-kpi" | "google-ads-impression-share" | "google-ads-keyword-table" | "google-ads-video-funnel" | "google-ads-shopping-table" | "pagespeed-scorecard-kpi" | "pagespeed-core-web-vitals" | "pagespeed-crux-history" | "ga4-traffic-overview-kpi" | "ga4-ecommerce-funnel" | "ga4-revenue" | "ga4-acquisition" | "youtube-overview-kpi" | "youtube-video-table" | "youtube-audience" | "linkedin-ads-overview-kpi" | "linkedin-ads-video-funnel" | "linkedin-ads-demographics" | "linkedin-ads-lead-gen" | "linkedin-pages-overview-kpi" | "linkedin-pages-demographics" | "meta-ads-overview-kpi" | "meta-ads-video-funnel" | "meta-ads-conversion-table" | "meta-ads-quality-table" | "microsoft-ads-overview-kpi" | "microsoft-ads-impression-share" | "microsoft-ads-keyword-quality-table" | "microsoft-ads-campaign-performance" | "microsoft-ads-pmax-final-url-table" | "pinterest-ads-overview-kpi" | "pinterest-ads-paid-vs-earned" | "pinterest-ads-saves-performance" | "pinterest-ads-video-funnel" | "pinterest-ads-conversion-roas-table" | "pinterest-ads-cross-device-attribution-table" | "snapchat-ads-overview-kpi" | "snapchat-ads-swipe-up-funnel" | "snapchat-ads-video-quartile-funnel" | "snapchat-ads-app-vs-web-conversions" | "snapchat-ads-demographic-breakdown" | "snapchat-ads-conversion-roas-table" | "reddit-ads-overview-kpi" | "reddit-ads-subreddit-performance" | "reddit-ads-conversion-attribution-funnel" | "reddit-ads-video-completion-funnel" | "reddit-ads-geo-country-region" | "reddit-ads-click-vs-view-attribution" | "amazon-ads-overview-kpi" | "amazon-ads-advertised-product-performance" | "amazon-ads-sb-ntb-funnel" | "amazon-ads-sd-click-vs-view" | "amazon-ads-dsp-audience-performance" | "amazon-ads-dsp-brand-store-engagement-funnel" | "amazon-ads-acos-roas-trend" | "amazon-ads-cross-surface-comparison" | "ahrefs-domain-rating-gauge" | "ahrefs-backlink-profile-overview" | "ahrefs-organic-traffic-value-trend" | "ahrefs-keyword-position-distribution" | "ahrefs-site-audit-health-score" | "ahrefs-ai-visibility-sov" | "ahrefs-web-analytics-overview" | "ahrefs-cross-endpoint-kpi-bundle" | "semrush-authority-score-gauge" | "semrush-backlinks-profile-overview" | "semrush-organic-traffic-cost-trend" | "semrush-position-changes-distribution" | "semrush-top-organic-keywords-table" | "semrush-top-organic-competitors-table" | "semrush-ai-visibility-mixed" | "semrush-cross-endpoint-kpi-bundle" | "moz-domain-authority-gauge" | "moz-page-authority-gauge" | "moz-brand-authority-gauge" | "moz-backlink-profile-overview" | "moz-top-pages-table" | "moz-top-linking-root-domains-table" | "moz-da-trend" | "moz-cross-endpoint-scorecard" | "callrail-call-volume-kpi" | "callrail-answer-rate-gauge" | "callrail-calls-over-time" | "callrail-calls-by-source-bar" | "callrail-calls-by-source-answer-rate-stacked" | "callrail-calls-list-table" | "callrail-conversation-intelligence-sentiment-pie" | "callrail-cross-endpoint-scorecard" | "ctm-call-volume-kpi" | "ctm-answer-rate-gauge" | "ctm-calls-over-time" | "ctm-calls-by-source-bar" | "ctm-calls-by-source-answer-rate-stacked" | "ctm-calls-list-table" | "ctm-conversation-intelligence-likelihood-quality-pie" | "ctm-cross-endpoint-scorecard" | "ctm-omnichannel-touch-funnel" | "mailchimp-revenue" | "mailchimp-benchmark" | "mailchimp-click-map" | "mailchimp-automation" | "klaviyo-overview-kpi" | "klaviyo-campaign-table" | "klaviyo-top-campaigns" | "klaviyo-flow-performance" | "klaviyo-form-conversion" | "klaviyo-segment-growth" | "hubspot-crm-pipeline-overview-kpi" | "hubspot-crm-deal-funnel" | "hubspot-crm-lifecycle-funnel" | "hubspot-crm-lead-source" | "hubspot-crm-deals-by-owner" | "hubspot-crm-revenue-trend" | "hubspot-crm-deals-table" | "hubspot-crm-lead-source-detail" | "hubspot-crm-first-vs-last-touch" | "hubspot-crm-attribution-trend" | "hubspot-email-overview" | "hubspot-email-campaign-table" | "hubspot-email-device-breakdown" | "hubspot-email-funnel" | "hubspot-traffic-overview" | "hubspot-traffic-sources" | "hubspot-traffic-trend" | "hubspot-campaign-roi" | "hubspot-campaign-attribution" | "hubspot-company-overview" | "hubspot-ticket-summary" | "hubspot-sales-activity" | "hubspot-form-performance" | "hubspot-event-attendance" | "hubspot-workflow-performance" | "hubspot-contact-geography" | "hubspot-deal-forecast" | "gbp-review-breakdown" | "gbp-search-keywords" | "seo-cross-tool-authority-scoreboard" | "seo-cross-tool-authority-bar-comparison" | "seo-cross-tool-authority-trend" | "google-reviews-overview-kpi" | "google-reviews-rating-distribution-bar" | "google-reviews-sentiment-pie" | "google-reviews-reply-rate-gauge" | "google-reviews-reviews-over-time" | "google-reviews-reviews-list-table" | "google-reviews-reply-moderation-state-pie" | "google-reviews-reputation-scorecard" | "trustpilot-overview-kpi" | "trustpilot-rating-distribution-bar" | "trustpilot-sentiment-pie" | "trustpilot-reply-rate-gauge" | "trustpilot-reviews-over-time" | "trustpilot-reviews-list-table" | "trustpilot-content-integrity-flag-pie" | "trustpilot-reputation-scorecard" | "yelp-overview-kpi" | "yelp-rating-distribution-bar" | "yelp-sentiment-pie" | "yelp-review-count-growth-line" | "yelp-rating-trend-over-time" | "yelp-tier-coverage-annotation" | "yelp-review-excerpts-list-table" | "yelp-reputation-scorecard" | "reviews-cross-platform-unified-scoreboard" | "reviews-cross-platform-rating-bar-comparison" | "reviews-cross-platform-review-velocity-trend" | "reviews-cross-platform-sentiment-stack" | "reviews-cross-platform-reply-rate-scoreboard" | "reviews-cross-platform-avg-response-time-bar" | "reviews-cross-platform-reply-rate-trend" | "reviews-cross-platform-awaiting-response-queue";

/** Union of widget types and preset types for palette items */
export type PaletteItemType = WidgetType | PresetType;

const PRESET_TYPES = new Set<string>(["email-overview-kpi", "email-campaign-table", "email-subscriber-growth", "email-top-campaigns", "ecommerce-sales-overview-kpi", "ecommerce-revenue-trend", "ecommerce-top-products", "ecommerce-orders-conversion", "ecommerce-product-performance", "ecommerce-profit", "ecommerce-customer", "ecommerce-cart-abandonment", "ecommerce-geo", "instagram-overview-kpi", "instagram-reels-table", "instagram-demographics", "facebook-pages-overview-kpi", "facebook-pages-video", "facebook-pages-reactions", "tiktok-ads-overview-kpi", "tiktok-ads-video-funnel", "tiktok-ads-campaign-table", "tiktok-organic-overview-kpi", "tiktok-organic-video-table", "google-ads-overview-kpi", "google-ads-impression-share", "google-ads-keyword-table", "google-ads-video-funnel", "google-ads-shopping-table", "pagespeed-scorecard-kpi", "pagespeed-core-web-vitals", "pagespeed-crux-history", "ga4-traffic-overview-kpi", "ga4-ecommerce-funnel", "ga4-revenue", "ga4-acquisition", "youtube-overview-kpi", "youtube-video-table", "youtube-audience", "linkedin-ads-overview-kpi", "linkedin-ads-video-funnel", "linkedin-ads-demographics", "linkedin-ads-lead-gen", "linkedin-pages-overview-kpi", "linkedin-pages-demographics", "meta-ads-overview-kpi", "meta-ads-video-funnel", "meta-ads-conversion-table", "meta-ads-quality-table", "microsoft-ads-overview-kpi", "microsoft-ads-impression-share", "microsoft-ads-keyword-quality-table", "microsoft-ads-campaign-performance", "microsoft-ads-pmax-final-url-table", "pinterest-ads-overview-kpi", "pinterest-ads-paid-vs-earned", "pinterest-ads-saves-performance", "pinterest-ads-video-funnel", "pinterest-ads-conversion-roas-table", "pinterest-ads-cross-device-attribution-table", "snapchat-ads-overview-kpi", "snapchat-ads-swipe-up-funnel", "snapchat-ads-video-quartile-funnel", "snapchat-ads-app-vs-web-conversions", "snapchat-ads-demographic-breakdown", "snapchat-ads-conversion-roas-table", "reddit-ads-overview-kpi", "reddit-ads-subreddit-performance", "reddit-ads-conversion-attribution-funnel", "reddit-ads-video-completion-funnel", "reddit-ads-geo-country-region", "reddit-ads-click-vs-view-attribution", "amazon-ads-overview-kpi", "amazon-ads-advertised-product-performance", "amazon-ads-sb-ntb-funnel", "amazon-ads-sd-click-vs-view", "amazon-ads-dsp-audience-performance", "amazon-ads-dsp-brand-store-engagement-funnel", "amazon-ads-acos-roas-trend", "amazon-ads-cross-surface-comparison", "ahrefs-domain-rating-gauge", "ahrefs-backlink-profile-overview", "ahrefs-organic-traffic-value-trend", "ahrefs-keyword-position-distribution", "ahrefs-site-audit-health-score", "ahrefs-ai-visibility-sov", "ahrefs-web-analytics-overview", "ahrefs-cross-endpoint-kpi-bundle", "semrush-authority-score-gauge", "semrush-backlinks-profile-overview", "semrush-organic-traffic-cost-trend", "semrush-position-changes-distribution", "semrush-top-organic-keywords-table", "semrush-top-organic-competitors-table", "semrush-ai-visibility-mixed", "semrush-cross-endpoint-kpi-bundle", "moz-domain-authority-gauge", "moz-page-authority-gauge", "moz-brand-authority-gauge", "moz-backlink-profile-overview", "moz-top-pages-table", "moz-top-linking-root-domains-table", "moz-da-trend", "moz-cross-endpoint-scorecard", "callrail-call-volume-kpi", "callrail-answer-rate-gauge", "callrail-calls-over-time", "callrail-calls-by-source-bar", "callrail-calls-by-source-answer-rate-stacked", "callrail-calls-list-table", "callrail-conversation-intelligence-sentiment-pie", "callrail-cross-endpoint-scorecard", "ctm-call-volume-kpi", "ctm-answer-rate-gauge", "ctm-calls-over-time", "ctm-calls-by-source-bar", "ctm-calls-by-source-answer-rate-stacked", "ctm-calls-list-table", "ctm-conversation-intelligence-likelihood-quality-pie", "ctm-cross-endpoint-scorecard", "ctm-omnichannel-touch-funnel", "mailchimp-revenue", "mailchimp-benchmark", "mailchimp-click-map", "mailchimp-automation", "klaviyo-overview-kpi", "klaviyo-campaign-table", "klaviyo-top-campaigns", "klaviyo-flow-performance", "klaviyo-form-conversion", "klaviyo-segment-growth", "hubspot-crm-pipeline-overview-kpi", "hubspot-crm-deal-funnel", "hubspot-crm-lifecycle-funnel", "hubspot-crm-lead-source", "hubspot-crm-deals-by-owner", "hubspot-crm-revenue-trend", "hubspot-crm-deals-table", "hubspot-crm-lead-source-detail", "hubspot-crm-first-vs-last-touch", "hubspot-crm-attribution-trend", "hubspot-email-overview", "hubspot-email-campaign-table", "hubspot-email-device-breakdown", "hubspot-email-funnel", "hubspot-traffic-overview", "hubspot-traffic-sources", "hubspot-traffic-trend", "hubspot-campaign-roi", "hubspot-campaign-attribution", "hubspot-company-overview", "hubspot-ticket-summary", "hubspot-sales-activity", "hubspot-form-performance", "hubspot-event-attendance", "hubspot-workflow-performance", "hubspot-contact-geography", "hubspot-deal-forecast", "gbp-review-breakdown", "gbp-search-keywords", "seo-cross-tool-authority-scoreboard", "seo-cross-tool-authority-bar-comparison", "seo-cross-tool-authority-trend", "google-reviews-overview-kpi", "google-reviews-rating-distribution-bar", "google-reviews-sentiment-pie", "google-reviews-reply-rate-gauge", "google-reviews-reviews-over-time", "google-reviews-reviews-list-table", "google-reviews-reply-moderation-state-pie", "google-reviews-reputation-scorecard", "trustpilot-overview-kpi", "trustpilot-rating-distribution-bar", "trustpilot-sentiment-pie", "trustpilot-reply-rate-gauge", "trustpilot-reviews-over-time", "trustpilot-reviews-list-table", "trustpilot-content-integrity-flag-pie", "trustpilot-reputation-scorecard", "yelp-overview-kpi", "yelp-rating-distribution-bar", "yelp-sentiment-pie", "yelp-review-count-growth-line", "yelp-rating-trend-over-time", "yelp-tier-coverage-annotation", "yelp-review-excerpts-list-table", "yelp-reputation-scorecard", "reviews-cross-platform-unified-scoreboard", "reviews-cross-platform-rating-bar-comparison", "reviews-cross-platform-review-velocity-trend", "reviews-cross-platform-sentiment-stack", "reviews-cross-platform-reply-rate-scoreboard", "reviews-cross-platform-avg-response-time-bar", "reviews-cross-platform-reply-rate-trend", "reviews-cross-platform-awaiting-response-queue"]);

/** Type guard to distinguish preset types from widget types */
export function isPresetType(type: string): type is PresetType {
  return PRESET_TYPES.has(type);
}

// ── Palette Item Definition ──────────────────────────────────────────

export interface PaletteItem {
  type: PaletteItemType;
  label: string;
  icon: PhosphorIcon;
  defaultColSpan: ValidColSpan;
  defaultRowSpan: number;
  /** Short tooltip description shown on hover (optional) */
  description?: string;
}

interface PaletteCategory {
  category: string;
  items: PaletteItem[];
}

// ── Category definitions ─────────────────────────────────────────────

interface CategoryDef {
  id: string;
  label: string;
  icon: PhosphorIcon;
  enabled: boolean;
}

export const ACTIVE_CATEGORIES: (CategoryDef & { items: PaletteItem[] })[] = [
  {
    id: "integrations-metrics",
    label: "Platform Widgets",
    icon: ChartDonutIcon,
    enabled: true,
    items: [
      { type: "cover-page", label: "Cover Page", icon: BookOpen, defaultColSpan: 12, defaultRowSpan: 3 },
      { type: "kpi", label: "KPI Card", icon: Hash, defaultColSpan: 3, defaultRowSpan: 1 },
      { type: "line-chart", label: "Line Chart", icon: TrendUp, defaultColSpan: 6, defaultRowSpan: 2 },
      { type: "bar-chart", label: "Bar Chart", icon: ChartBar, defaultColSpan: 6, defaultRowSpan: 2 },
      { type: "pie-chart", label: "Pie/Donut Chart", icon: ChartPieSlice, defaultColSpan: 4, defaultRowSpan: 2 },
      { type: "funnel", label: "Funnel Chart", icon: Funnel, defaultColSpan: 6, defaultRowSpan: 3 },
      { type: "demographic", label: "Demographics", icon: Users, defaultColSpan: 6, defaultRowSpan: 2 },
      { type: "top-posts", label: "Top Posts", icon: Newspaper, defaultColSpan: 6, defaultRowSpan: 4 },
      { type: "data-table", label: "Data Table", icon: Table, defaultColSpan: 12, defaultRowSpan: 3 },
      { type: "rollup-table", label: "Rollup Table", icon: Table, defaultColSpan: 12, defaultRowSpan: 4 },
      { type: "text-block", label: "Text Block", icon: TextT, defaultColSpan: 12, defaultRowSpan: 2 },
    ],
  },
  {
    id: "content-blocks",
    label: "Page Elements",
    icon: GridNineIcon,
    enabled: true,
    items: [
      { type: "heading", label: "Heading", icon: TextHOne, defaultColSpan: 12, defaultRowSpan: 1 },
      { type: "paragraph", label: "Paragraph", icon: TextAlignLeft, defaultColSpan: 12, defaultRowSpan: 2 },
      { type: "rich-text", label: "Rich Text", icon: FileText, defaultColSpan: 12, defaultRowSpan: 3 },
      { type: "spacer-divider", label: "Spacer / Divider", icon: Minus, defaultColSpan: 12, defaultRowSpan: 1 },
    ],
  },
  {
    id: "images",
    label: "Images",
    icon: Image,
    enabled: true,
    items: [
      { type: "image", label: "Image", icon: Image, defaultColSpan: 6, defaultRowSpan: 3 },
    ],
  },
  {
    id: "embeds",
    label: "Embeds",
    icon: Code,
    enabled: true,
    items: [
      { type: "embed", label: "Embed", icon: Code, defaultColSpan: 12, defaultRowSpan: 3 },
    ],
  },
  {
    id: "custom-metrics",
    label: "Custom Metrics",
    icon: RulerIcon,
    enabled: true,
    items: [
      { type: "custom-metric", label: "Custom Metric", icon: RulerIcon, defaultColSpan: 3, defaultRowSpan: 1, description: "User-defined calculated metric from multiple data sources" },
    ],
  },
  {
    id: "benchmarks",
    label: "Benchmarks",
    icon: GaugeIcon,
    enabled: true,
    items: [
      { type: "benchmark", label: "Industry Benchmark", icon: GaugeIcon, defaultColSpan: 6, defaultRowSpan: 4, description: "Compare client metrics to industry averages" },
    ],
  },
  {
    id: "goals",
    label: "Goals",
    icon: Flag,
    enabled: true,
    items: [
      { type: "goal", label: "Goal Tracker", icon: Flag, defaultColSpan: 4, defaultRowSpan: 2, description: "Track progress toward goals and budgets with visual indicators" },
    ],
  },
  {
    id: "email-marketing",
    label: "Email Marketing",
    icon: Envelope,
    enabled: true,
    items: [
      // Shared / Mailchimp presets (hidden in WidgetCardsPanel when only Klaviyo is connected)
      { type: "email-overview-kpi", label: "Email Overview", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 1, description: "Adds 5 KPI widgets: subscribers, emails sent, open rate, click rate, unsub rate" },
      { type: "email-campaign-table", label: "Campaign Performance", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Campaign metrics table with send date, open/click rates" },
      { type: "email-subscriber-growth", label: "Subscriber Growth", icon: TrendUp, defaultColSpan: 6, defaultRowSpan: 3, description: "Subscriber trend line chart over time" },
      { type: "email-top-campaigns", label: "Top Campaigns", icon: Trophy, defaultColSpan: 12, defaultRowSpan: 3, description: "Best performing campaigns sorted by open rate, click rate, or other metrics" },
      { type: "mailchimp-revenue", label: "E-commerce Revenue", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 1, description: "3 KPI widgets: revenue, orders, avg order revenue from Mailchimp e-commerce" },
      { type: "mailchimp-benchmark", label: "Industry Benchmarks", icon: GaugeIcon, defaultColSpan: 12, defaultRowSpan: 4, description: "Compare your open & click rates to industry averages" },
      { type: "mailchimp-click-map", label: "URL Click Map", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Per-URL click performance table for each campaign" },
      { type: "mailchimp-automation", label: "Automation Performance", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Per-workflow automation metrics: opens, clicks, rates" },
      // Story 12.8: Klaviyo-namespaced presets (hidden unless Klaviyo is connected)
      { type: "klaviyo-overview-kpi", label: "Klaviyo Overview", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: list members, emails delivered, open rate, click rate, unsub rate" },
      { type: "klaviyo-campaign-table", label: "Klaviyo Campaign Performance", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Campaign table (period total): recipients, open/click rate, revenue" },
      { type: "klaviyo-top-campaigns", label: "Klaviyo Top Campaigns", icon: Trophy, defaultColSpan: 12, defaultRowSpan: 3, description: "Best email campaigns sorted by open rate, with privacy disclaimer" },
      { type: "klaviyo-flow-performance", label: "Klaviyo Flow Performance", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Per-flow message table (period total): recipients, engagement, revenue" },
      { type: "klaviyo-form-conversion", label: "Klaviyo Form Conversion", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 1, description: "4 KPI widgets: form views, submissions, submit rate, qualified visitors" },
      { type: "klaviyo-segment-growth", label: "Klaviyo Segment Growth", icon: TrendUp, defaultColSpan: 6, defaultRowSpan: 3, description: "Net members change per segment over the period" },
    ],
  },
  {
    id: "ecommerce",
    label: "E-commerce",
    icon: ShoppingCart,
    enabled: true,
    items: [
      { type: "ecommerce-sales-overview-kpi", label: "Sales Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 2, description: "5-card KPI summary: revenue, orders, AOV, conversion rate, customers" },
      { type: "ecommerce-revenue-trend", label: "Revenue Trend", icon: TrendUp, defaultColSpan: 6, defaultRowSpan: 3, description: "Daily revenue line chart over time" },
      { type: "ecommerce-top-products", label: "Top Products", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Top 10 products ranked by revenue with units sold and % of total" },
      { type: "ecommerce-orders-conversion", label: "Orders & Conversion", icon: ChartBar, defaultColSpan: 6, defaultRowSpan: 3, description: "Daily orders bar chart with comparison period overlay" },
      { type: "ecommerce-product-performance", label: "Product Performance", icon: Table, defaultColSpan: 12, defaultRowSpan: 4, description: "Detailed product table with SKU, revenue, units, avg price, and trend badges" },
      { type: "ecommerce-profit", label: "Profit Metrics", icon: ChartDonutIcon, defaultColSpan: 12, defaultRowSpan: 1, description: "3 KPIs: gross profit, gross margin, cost of goods sold" },
      { type: "ecommerce-customer", label: "Customer Insights", icon: Users, defaultColSpan: 12, defaultRowSpan: 1, description: "4 KPIs: new customers, returning customers, CLV, avg orders per customer" },
      { type: "ecommerce-cart-abandonment", label: "Cart Abandonment", icon: ShoppingCart, defaultColSpan: 12, defaultRowSpan: 1, description: "3 KPIs: cart abandonment rate, abandoned checkout count and value" },
      { type: "ecommerce-geo", label: "Sales by Geography", icon: Buildings, defaultColSpan: 12, defaultRowSpan: 3, description: "Revenue by country bar chart + detailed breakdown table" },
    ],
  },
  {
    id: "instagram",
    label: "Instagram",
    icon: InstagramLogo,
    enabled: true,
    items: [
      { type: "instagram-overview-kpi", label: "Instagram Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: followers, reach, views, engagement rate, profile link taps" },
      { type: "instagram-reels-table", label: "Reels Performance", icon: VideoCamera, defaultColSpan: 12, defaultRowSpan: 3, description: "Reel-by-reel table: views, likes, comments, shares, saves, avg watch time, skip rate" },
      { type: "instagram-demographics", label: "Follower Demographics", icon: Users, defaultColSpan: 6, defaultRowSpan: 2, description: "Follower age, gender, and country breakdown" },
    ],
  },
  {
    id: "facebook-pages",
    label: "Facebook Pages",
    icon: FacebookLogo,
    enabled: true,
    items: [
      { type: "facebook-pages-overview-kpi", label: "Facebook Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: followers, media views, unique viewers, engagements, engagement rate" },
      { type: "facebook-pages-video", label: "Video Performance", icon: VideoCamera, defaultColSpan: 6, defaultRowSpan: 3, description: "Video views line chart + paid vs organic bar chart" },
      { type: "facebook-pages-reactions", label: "Reactions Breakdown", icon: Heart, defaultColSpan: 6, defaultRowSpan: 3, description: "Bar chart: like, love, wow, haha, sad, angry reactions" },
    ],
  },
  {
    id: "meta-ads",
    label: "Meta Ads",
    icon: Megaphone,
    enabled: true,
    items: [
      { type: "meta-ads-overview-kpi", label: "Meta Ads Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: spend, impressions, reach, CTR, ROAS" },
      { type: "meta-ads-video-funnel", label: "Video Funnel", icon: Funnel, defaultColSpan: 6, defaultRowSpan: 3, description: "Video completion funnel: plays → 25% → 50% → 75% → 100%" },
      { type: "meta-ads-conversion-table", label: "Conversion Performance", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Campaign table: spend, conversions, cost/conversion, ROAS, LP view/click" },
      { type: "meta-ads-quality-table", label: "Quality Rankings", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Ad quality table: quality, engagement rate, conversion rate rankings" },
    ],
  },
  {
    id: "tiktok-ads",
    label: "TikTok Ads",
    icon: TiktokLogo,
    enabled: true,
    items: [
      { type: "tiktok-ads-overview-kpi", label: "TikTok Ads Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: spend, impressions, clicks, CTR, conversions" },
      { type: "tiktok-ads-video-funnel", label: "Video View Funnel", icon: Funnel, defaultColSpan: 6, defaultRowSpan: 3, description: "Video funnel: plays → 6s views → 50% → 100% completion" },
      { type: "tiktok-ads-campaign-table", label: "Campaign Performance", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Campaign table: spend, clicks, conversions, cost/conversion, ROAS" },
      { type: "tiktok-organic-overview-kpi", label: "TikTok Organic Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: followers, views, engagement, engagement rate, avg views" },
      { type: "tiktok-organic-video-table", label: "Video Performance", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Video table: views, likes, comments, shares, saves, avg watch time" },
    ],
  },
  {
    id: "google-ads",
    label: "Google Ads",
    icon: GoogleLogo,
    enabled: true,
    items: [
      { type: "google-ads-overview-kpi", label: "Google Ads Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: cost, clicks, conversions, ROAS, CTR" },
      { type: "google-ads-impression-share", label: "Impression Share", icon: GaugeIcon, defaultColSpan: 12, defaultRowSpan: 3, description: "Search IS KPI + bar chart: lost IS by budget vs rank" },
      { type: "google-ads-keyword-table", label: "Keyword Performance", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Keyword table: impressions, clicks, CTR, quality score, cost, conversions" },
      { type: "google-ads-video-funnel", label: "Video Funnel", icon: Funnel, defaultColSpan: 6, defaultRowSpan: 3, description: "Video funnel: views → 25% → 50% → 75% → 100%" },
      { type: "google-ads-shopping-table", label: "Shopping Performance", icon: ShoppingCart, defaultColSpan: 12, defaultRowSpan: 3, description: "Product table: title, revenue, units sold, gross profit, AOV, COGS" },
    ],
  },
  {
    id: "pagespeed",
    label: "PageSpeed",
    icon: Lightning,
    enabled: true,
    items: [
      { type: "pagespeed-scorecard-kpi", label: "PageSpeed Scorecard", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: Performance, Accessibility, SEO, Best Practices, PWA scores" },
      { type: "pagespeed-core-web-vitals", label: "Core Web Vitals", icon: GaugeIcon, defaultColSpan: 12, defaultRowSpan: 1, description: "4 KPI widgets: LCP, FCP, CLS, INP with CWV good/poor thresholds" },
      { type: "pagespeed-crux-history", label: "CrUX History Trend", icon: TrendUp, defaultColSpan: 6, defaultRowSpan: 3, description: "40-week trend: LCP, FCP, CLS, INP p75 values over time" },
    ],
  },
  {
    id: "ga4",
    label: "Google Analytics 4",
    icon: GoogleLogo,
    enabled: true,
    items: [
      { type: "ga4-traffic-overview-kpi", label: "Traffic Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: Sessions, Users, New Users, Bounce Rate, Engagement Rate" },
      { type: "ga4-ecommerce-funnel", label: "E-commerce Funnel", icon: Funnel, defaultColSpan: 6, defaultRowSpan: 3, description: "Funnel: Items Viewed → Add to Cart → Checkouts → Purchases" },
      { type: "ga4-revenue", label: "Revenue Dashboard", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "Revenue trend line-chart + KPIs: Revenue/User, Purchases, Purchaser Rate" },
      { type: "ga4-acquisition", label: "Acquisition Report", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 3, description: "Sessions by channel bar-chart + source/medium data-table" },
    ],
  },
  {
    id: "youtube",
    label: "YouTube",
    icon: YoutubeLogo,
    enabled: true,
    items: [
      { type: "youtube-overview-kpi", label: "YouTube Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: Views, Watch Time, Subscribers Gained, Likes, Thumbnail CTR" },
      { type: "youtube-video-table", label: "Top Videos", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Video table: views, avg view duration, likes, comments, shares" },
      { type: "youtube-audience", label: "Audience Demographics", icon: Users, defaultColSpan: 6, defaultRowSpan: 2, description: "Age × gender viewer percentage breakdown" },
    ],
  },
  {
    id: "gbp",
    label: "Google Business Profile",
    icon: MapPin,
    enabled: true,
    items: [
      { type: "gbp-review-breakdown", label: "Review Breakdown", icon: Star, defaultColSpan: 6, defaultRowSpan: 2, description: "Bar chart: reviews by star rating (1-5 stars)" },
      { type: "gbp-search-keywords", label: "Search Keywords", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Top 20 search keywords by impressions" },
    ],
  },
  {
    id: "linkedin-pages",
    label: "LinkedIn Pages",
    icon: Buildings,
    enabled: true,
    items: [
      { type: "linkedin-pages-overview-kpi", label: "LinkedIn Pages Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: Followers, Impressions, Clicks, Engagement Rate, Page Views" },
      { type: "linkedin-pages-demographics", label: "Follower Demographics", icon: Users, defaultColSpan: 6, defaultRowSpan: 3, description: "Follower breakdown by Industry, Seniority, and Job Function" },
    ],
  },
  {
    id: "linkedin-ads",
    label: "LinkedIn Ads",
    icon: LinkedinLogo,
    enabled: true,
    items: [
      { type: "linkedin-ads-overview-kpi", label: "LinkedIn Ads Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: Spend, Impressions, Clicks, CTR, Conversions" },
      { type: "linkedin-ads-video-funnel", label: "Video Funnel", icon: Funnel, defaultColSpan: 6, defaultRowSpan: 3, description: "Video funnel: Starts → Views → 50% → Completed" },
      { type: "linkedin-ads-demographics", label: "Professional Demographics", icon: Buildings, defaultColSpan: 6, defaultRowSpan: 3, description: "Impressions by Industry + Seniority breakdown" },
      { type: "linkedin-ads-lead-gen", label: "Lead Generation", icon: Users, defaultColSpan: 12, defaultRowSpan: 1, description: "4 KPI widgets: Leads, Cost per Lead, Qualified Leads, Form Opens" },
    ],
  },
  {
    id: "microsoft-ads",
    label: "Microsoft Ads",
    icon: WindowsLogo,
    enabled: true,
    items: [
      { type: "microsoft-ads-overview-kpi", label: "Microsoft Ads Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: Spend, Impressions, Clicks, CTR, Conversions" },
      { type: "microsoft-ads-impression-share", label: "Impression Share", icon: GaugeIcon, defaultColSpan: 12, defaultRowSpan: 3, description: "Gauge for IS% + bar chart of IS lost to Budget vs Rank" },
      { type: "microsoft-ads-keyword-quality-table", label: "Quality Score Keywords", icon: Star, defaultColSpan: 12, defaultRowSpan: 4, description: "Top keywords with QS, expected CTR, ad relevance, LP experience" },
      { type: "microsoft-ads-campaign-performance", label: "Campaign Performance", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Campaign breakdown: spend, conversions, ROAS, impression share" },
      { type: "microsoft-ads-pmax-final-url-table", label: "PMax Final URL", icon: LinkSimple, defaultColSpan: 12, defaultRowSpan: 3, description: "Performance Max landing page reporting (April 2026 launch)" },
    ],
  },
  {
    // Story 19.6: Pinterest Ads palette section. PinterestLogo is not yet
    // ported in @phosphor-icons/react v2.1.10 — fall back to BookmarkSimple
    // (Pinterest's iconic save action) per the story's icon-fallback note.
    id: "pinterest-ads",
    label: "Pinterest Ads",
    icon: BookmarkSimple,
    enabled: true,
    items: [
      { type: "pinterest-ads-overview-kpi", label: "Pinterest Ads Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: Spend, Total Impressions, Engagements, Saves, ROAS" },
      { type: "pinterest-ads-paid-vs-earned", label: "Paid vs Earned Impressions", icon: ArrowsLeftRight, defaultColSpan: 12, defaultRowSpan: 3, description: "Stacked chart of paid + earned impressions over time" },
      { type: "pinterest-ads-saves-performance", label: "Saves Performance", icon: BookmarkSimple, defaultColSpan: 12, defaultRowSpan: 4, description: "Save rate KPI + saves trend + per-pin saves breakdown" },
      { type: "pinterest-ads-video-funnel", label: "Pinterest Video Funnel", icon: FilmStrip, defaultColSpan: 12, defaultRowSpan: 4, description: "Video funnel: Started (P0) → 25% → 50% → 75% → 95% → Complete" },
      { type: "pinterest-ads-conversion-roas-table", label: "Conversion ROAS Table", icon: Trophy, defaultColSpan: 12, defaultRowSpan: 3, description: "Campaign breakdown: spend, conversions, multiple ROAS variants" },
      { type: "pinterest-ads-cross-device-attribution-table", label: "Cross-Device Attribution", icon: DeviceMobile, defaultColSpan: 12, defaultRowSpan: 3, description: "Web / In-App / Pinterest-native split — Pinterest's unique attribution view" },
    ],
  },
  {
    // Story 19.9: Snapchat Ads palette section. SnapchatLogo from
    // @phosphor-icons/react v2.1.10 — verified available; no fallback needed.
    id: "snapchat-ads",
    label: "Snapchat Ads",
    icon: SnapchatLogo,
    enabled: true,
    items: [
      { type: "snapchat-ads-overview-kpi", label: "Snapchat Ads Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: Spend, Impressions, Swipe-Ups, Swipe-Up Rate, Purchase Value" },
      { type: "snapchat-ads-swipe-up-funnel", label: "Snap Swipe-Up Funnel", icon: ChartBarHorizontal, defaultColSpan: 12, defaultRowSpan: 4, description: "5-stage funnel: Impressions → Swipe-Ups → Page Views → Add Cart → Purchases" },
      { type: "snapchat-ads-video-quartile-funnel", label: "Snap Video Quartile Funnel", icon: FilmStrip, defaultColSpan: 12, defaultRowSpan: 4, description: "Video drop-off: 2s Views → 25% → 50% → 75% → 100% Complete" },
      { type: "snapchat-ads-app-vs-web-conversions", label: "App vs Web Conversions", icon: Devices, defaultColSpan: 12, defaultRowSpan: 4, description: "Snap's unique App/Web conversion split — competitive moat vs Meta/Google" },
      { type: "snapchat-ads-demographic-breakdown", label: "Snap Demographic Breakdown", icon: Users, defaultColSpan: 12, defaultRowSpan: 4, description: "Age × Gender breakdown including the Feb 2025 55+ bucket" },
      { type: "snapchat-ads-conversion-roas-table", label: "Snap Conversion ROAS Table", icon: Trophy, defaultColSpan: 12, defaultRowSpan: 3, description: "Per-campaign App/Web split + totals + Snap's signature swipe-up metrics" },
    ],
  },
  {
    // Story 19.12: Reddit Ads palette section. RedditLogo from
    // @phosphor-icons/react v2.1.10 — verified available; no fallback needed.
    id: "reddit-ads",
    label: "Reddit Ads",
    icon: RedditLogo,
    enabled: true,
    items: [
      { type: "reddit-ads-overview-kpi", label: "Reddit Ads Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPIs: Spend, Impressions, Clicks, CTR, Key Conversions" },
      { type: "reddit-ads-subreddit-performance", label: "Subreddit Performance", icon: ListBullets, defaultColSpan: 12, defaultRowSpan: 4, description: "Per-subreddit performance — Reddit's signature differentiator" },
      { type: "reddit-ads-conversion-attribution-funnel", label: "Conversion Funnel (Click)", icon: ChartBarHorizontal, defaultColSpan: 12, defaultRowSpan: 4, description: "5-stage click-attributed funnel: Impressions → Purchases" },
      { type: "reddit-ads-video-completion-funnel", label: "Reddit Video Completion", icon: FilmStrip, defaultColSpan: 12, defaultRowSpan: 4, description: "6-stage video drop-off: Started → 3s → 25% → 50% → 75% → 100%" },
      { type: "reddit-ads-geo-country-region", label: "Geo by Country", icon: Globe, defaultColSpan: 12, defaultRowSpan: 4, description: "Country × Region — Reddit's only combined-4-breakdown allowed" },
      { type: "reddit-ads-click-vs-view-attribution", label: "Click vs View Attribution", icon: ArrowsLeftRight, defaultColSpan: 12, defaultRowSpan: 4, description: "Click-vs-view attribution split — Reddit's Aug 2025 dimension" },
    ],
  },
  {
    // Story 19.17: Amazon Ads palette section. AmazonLogo from
    // @phosphor-icons/react v2.1+ — verified available; no fallback needed.
    // Closes Epic 19 (Microsoft Ads + Pinterest Ads + Snapchat Ads + Reddit
    // Ads + Amazon Ads — all 5 PPC integrations now ship at full parity).
    id: "amazon-ads",
    label: "Amazon Ads",
    icon: AmazonLogo,
    enabled: true,
    items: [
      { type: "amazon-ads-overview-kpi", label: "Amazon Ads Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPIs: Spend, Impressions, Clicks, ACOS, ROAS" },
      { type: "amazon-ads-advertised-product-performance", label: "Advertised Product Performance", icon: Storefront, defaultColSpan: 12, defaultRowSpan: 4, description: "ASIN-level table — Amazon's signature reporting surface" },
      { type: "amazon-ads-sb-ntb-funnel", label: "SB New-to-Brand Funnel", icon: Users, defaultColSpan: 12, defaultRowSpan: 4, description: "5-stage NTB funnel — Sponsored Brands branded discovery" },
      { type: "amazon-ads-sd-click-vs-view", label: "SD Click vs View Attribution", icon: ArrowsLeftRight, defaultColSpan: 12, defaultRowSpan: 4, description: "Click vs view-through — SD's view-through retargeting moat" },
      { type: "amazon-ads-dsp-audience-performance", label: "DSP Audience Performance", icon: Target, defaultColSpan: 12, defaultRowSpan: 4, description: "AMC + Lookalike + 3P segment performance — DSP's flagship surface" },
      { type: "amazon-ads-dsp-brand-store-engagement-funnel", label: "DSP Brand Store Engagement", icon: Stack, defaultColSpan: 12, defaultRowSpan: 4, description: "5-stage brand store deep-link drop-off (levels 1-7 grid)" },
      { type: "amazon-ads-acos-roas-trend", label: "ACOS / ROAS Trend", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "Dual-axis 90-day trend — agency executive dashboard signature" },
      { type: "amazon-ads-cross-surface-comparison", label: "Cross-Surface Comparison (SP/SB/SD)", icon: ChartPieSlice, defaultColSpan: 12, defaultRowSpan: 4, description: "Multi-platform reach moat — only ProvenLeap surfaces this in 1 widget" },
    ],
  },
  {
    // Story 20.3: Ahrefs — opens the SEO category, closes Epic 20.
    // 8 widget presets spanning all 5 endpoint families (Site Explorer +
    // Rank Tracker + Site Audit + Brand Radar + Web Analytics).
    id: "ahrefs",
    label: "Ahrefs",
    icon: Globe,
    enabled: true,
    items: [
      { type: "ahrefs-domain-rating-gauge", label: "Domain Rating Gauge", icon: GaugeIcon, defaultColSpan: 6, defaultRowSpan: 2, description: "DR 0-100 — the universal SEO headline metric" },
      { type: "ahrefs-backlink-profile-overview", label: "Backlink Profile Overview", icon: GitBranch, defaultColSpan: 12, defaultRowSpan: 1, description: "4 KPIs: Backlinks, Ref Domains, Dofollow Backlinks, Dofollow Ref Domains" },
      { type: "ahrefs-organic-traffic-value-trend", label: "Organic Traffic Value Trend", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "Dual-axis 90-day trend — 'we 3xed your traffic value' executive line" },
      { type: "ahrefs-keyword-position-distribution", label: "Keyword Position Distribution", icon: ChartBar, defaultColSpan: 6, defaultRowSpan: 3, description: "5-bucket distribution: Top 3 / 4-10 / 11-20 / 21-50 / 51+" },
      { type: "ahrefs-site-audit-health-score", label: "Site Audit Health Score", icon: Heartbeat, defaultColSpan: 6, defaultRowSpan: 2, description: "Health Score 0-100 — the technical-SEO headline gauge" },
      { type: "ahrefs-ai-visibility-sov", label: "AI Visibility — Share of Voice", icon: Robot, defaultColSpan: 6, defaultRowSpan: 2, description: "Brand presence in ChatGPT/Gemini/Perplexity/Copilot/AI Overviews — 2026 flagship" },
      { type: "ahrefs-web-analytics-overview", label: "Web Analytics Overview", icon: ChartLineUp, defaultColSpan: 12, defaultRowSpan: 1, description: "4 KPIs: Pageviews, Visitors, Visits, Bounce Rate" },
      { type: "ahrefs-cross-endpoint-kpi-bundle", label: "Cross-Endpoint KPI Bundle", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "C-suite scorecard: DR + Traffic Value + AI SoV + Health Score" },
    ],
  },
  {
    // Story 20.6: Semrush — second SEO entry (after Ahrefs, before Moz 20.8).
    // Closes Epic 20 Semrush slice. 8 widget presets spanning all 10
    // `_reportType` discriminators (Domain Overview + Backlinks Overview +
    // Phrase KDI + Trends Summary + Domain Organic + Domain Adwords +
    // Phrase Related + Phrase Questions + Domain Rank History + Domain
    // Organic Competitors).
    id: "semrush",
    label: "Semrush",
    icon: MagnifyingGlass,
    enabled: true,
    items: [
      { type: "semrush-authority-score-gauge", label: "Authority Score Gauge", icon: GaugeIcon, defaultColSpan: 6, defaultRowSpan: 2, description: "Authority Score 0-100 — Semrush's universal SEO headline metric" },
      { type: "semrush-backlinks-profile-overview", label: "Backlinks Profile Overview", icon: GitBranch, defaultColSpan: 12, defaultRowSpan: 1, description: "4 KPIs: Backlinks, Ref Domains, Dofollow, Ref IPs" },
      { type: "semrush-organic-traffic-cost-trend", label: "Organic Traffic & Cost Trend", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "Dual-axis 24-month trend — 'we 2× your organic-traffic-cost equivalent' line" },
      { type: "semrush-position-changes-distribution", label: "Position Changes Distribution", icon: ChartBar, defaultColSpan: 6, defaultRowSpan: 3, description: "4-bucket: New / Improved / Declined / Lost (Semrush KB-495)" },
      { type: "semrush-top-organic-keywords-table", label: "Top Organic Keywords", icon: Table, defaultColSpan: 12, defaultRowSpan: 4, description: "Per-keyword Top 25 with position-Δ chevron + AI Overview badge" },
      { type: "semrush-top-organic-competitors-table", label: "Top Organic Competitors", icon: UsersFour, defaultColSpan: 12, defaultRowSpan: 3, description: "Top 25 competitors by relevance — keyword overlap analysis" },
      { type: "semrush-ai-visibility-mixed", label: "AI Visibility — Mixed Surface", icon: Robot, defaultColSpan: 12, defaultRowSpan: 1, description: "Trends API AI Assistants + AI Search + AI Overview presence rate" },
      { type: "semrush-cross-endpoint-kpi-bundle", label: "Cross-Endpoint KPI Bundle", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "C-suite scorecard: AS + Traffic Cost + AI Assistants + Position Δ" },
    ],
  },
  {
    // Story 20.8 — Moz (THIRD SEO entry; closes Epic 20 Moz slice). Per
    // AgencyAnalytics + Databox + DashThis + Reliqus 2026 Moz dashboard
    // templates (DA gauge / PA gauge / BA gauge / 4-KPI backlinks / Top
    // Pages / Top Linking Root Domains / DA Trend / Cross-Endpoint).
    id: "moz",
    label: "Moz",
    icon: Anchor,
    enabled: true,
    items: [
      { type: "moz-domain-authority-gauge", label: "Domain Authority Gauge", icon: GaugeIcon, defaultColSpan: 6, defaultRowSpan: 2, description: "DA 0-100 — Moz's flagship SEO authority metric since 2010" },
      { type: "moz-page-authority-gauge", label: "Page Authority Gauge", icon: GaugeIcon, defaultColSpan: 6, defaultRowSpan: 2, description: "PA 0-100 — the page-level companion to DA" },
      { type: "moz-brand-authority-gauge", label: "Brand Authority Gauge", icon: Crown, defaultColSpan: 6, defaultRowSpan: 2, description: "BA 0-100 — 2026 flagship AI-era brand-recognition signal" },
      { type: "moz-backlink-profile-overview", label: "Backlink Profile Overview", icon: GitBranch, defaultColSpan: 12, defaultRowSpan: 1, description: "4 KPIs: Inbound Pages, External Pages, Root Domains, Nofollow Root Domains" },
      { type: "moz-top-pages-table", label: "Top Pages", icon: Table, defaultColSpan: 12, defaultRowSpan: 4, description: "Top 50 pages by Page Authority — agency-canonical page-level analysis" },
      { type: "moz-top-linking-root-domains-table", label: "Top Linking Root Domains", icon: ListNumbers, defaultColSpan: 12, defaultRowSpan: 3, description: "Top 25 referring domains by Source Domain Authority" },
      { type: "moz-da-trend", label: "Domain Authority Trend", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "6-month DA history — ProvenLeap-persisted snapshot trend (Moz V3 has no time-series API)" },
      { type: "moz-cross-endpoint-scorecard", label: "Cross-Endpoint Scorecard", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "C-suite scorecard: DA + BA + Linking Root Domains + DA Trend Direction" },
    ],
  },
  {
    // Story 20.9 — SEO Cross-Tool (FOURTH and FINAL SEO entry; closes
    // Epic 20). The category-defining cross-tool authority comparison —
    // no competitor reporting tool surfaces all 3 industry-standard
    // authority scores side-by-side per AgencyAnalytics + Whatagraph +
    // DashThis + Databox + Reliqus May 2026 competitive review.
    // Runtime-gated by all-3-SEO-tools-connected per AC #1 (see
    // `filterPaletteSectionsByConnectedPlatforms` helper below).
    id: "seo-cross-tool",
    label: "SEO Cross-Tool",
    icon: GitBranch,
    enabled: true,
    items: [
      { type: "seo-cross-tool-authority-scoreboard", label: "Authority Scoreboard", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "3 side-by-side KPIs: Ahrefs DR + Semrush AS + Moz DA — the agency client's most-asked SEO question" },
      { type: "seo-cross-tool-authority-bar-comparison", label: "Authority Bar Comparison", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 3, description: "3-bar chart on the same 0-100 scale — visualizes calibration divergence (DR > AS > DA)" },
      { type: "seo-cross-tool-authority-trend", label: "6-Month Authority Trend", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "3-line chart joining Ahrefs DR + Semrush AS + Moz DA over 6 months — category-defining differentiator (no competitor surfaces this)" },
    ],
  },
  {
    // Story 22.2 — Call Tracking (opens Epic 22 demo surface; FIRST entry in
    // this category). CallTrackingMetrics joins later via Story 22.4. Build-
    // to-sell category-defining widget = preset #5 (Calls × Source × Answer
    // Rate stacked-bar — no competitor reporting tool surfaces this cross-
    // tab in one click per AgencyAnalytics + Databox + Whatagraph + DashThis
    // + Swydo May 2026 dashboards verified 2026-05-15).
    id: "call-tracking",
    label: "Call Tracking",
    icon: Phone, // Story 22.2 Pass 1 — Phosphor canonical phone glyph per AC #11
    enabled: true,
    items: [
      { type: "callrail-call-volume-kpi", label: "Call Volume KPI", icon: PhoneIncoming, defaultColSpan: 12, defaultRowSpan: 1, description: "4 KPIs: Total Calls, Answered, Missed, First-Time Callers" },
      { type: "callrail-answer-rate-gauge", label: "Answer Rate Gauge", icon: GaugeIcon, defaultColSpan: 6, defaultRowSpan: 2, description: "Answer Rate 0-100% — the call-tracking quality benchmark" },
      { type: "callrail-calls-over-time", label: "Calls Over Time", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "90-day daily call-volume trend — agency-canonical cover-page widget" },
      { type: "callrail-calls-by-source-bar", label: "Calls by Source", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 3, description: "Per-source call volume — the #1 attribution widget" },
      { type: "callrail-calls-by-source-answer-rate-stacked", label: "Calls × Source × Answer Rate", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 3, description: "Source quality cross-tab — answers 'which channels drive HIGH-answer-rate calls?'" },
      { type: "callrail-calls-list-table", label: "Calls List", icon: Table, defaultColSpan: 12, defaultRowSpan: 4, description: "Per-call records — duration / source / answer-status / value / lead-status" },
      { type: "callrail-conversation-intelligence-sentiment-pie", label: "Sentiment Distribution", icon: ChartPieSlice, defaultColSpan: 6, defaultRowSpan: 3, description: "Positive / Neutral / Negative call sentiment — 2026 AI-era CallRail flagship" },
      { type: "callrail-cross-endpoint-scorecard", label: "Cross-Endpoint Scorecard", icon: PhoneCall, defaultColSpan: 12, defaultRowSpan: 1, description: "C-suite scorecard: Total Calls + Answer Rate + First-Time Caller Rate + Lead Conversion Rate" },
      // Story 22.4 — CTM presets join the call-tracking section. CallRail
      // (22.2) and CTM (22.4) live side-by-side as the only entries in
      // this palette section. The section grows from 8 → 17 items (largest
      // single-vertical section after this lands). Build-to-sell preset =
      // ctm-omnichannel-touch-funnel — no competitor reporting tool
      // surfaces Calls + Forms + SMS in 1 widget per AgencyAnalytics +
      // Databox + Whatagraph + DashThis + Swydo May 2026.
      { type: "ctm-call-volume-kpi", label: "CTM Call Volume KPI", icon: PhoneIncoming, defaultColSpan: 12, defaultRowSpan: 1, description: "4 KPIs: Total Calls, Answered, Missed, New Callers" },
      { type: "ctm-answer-rate-gauge", label: "CTM Answer Rate Gauge", icon: GaugeIcon, defaultColSpan: 6, defaultRowSpan: 2, description: "Answer Rate 0-100% — CTM omnichannel quality benchmark" },
      { type: "ctm-calls-over-time", label: "CTM Calls Over Time", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "90-day daily call trend — client-side bucketed from /calls.json (CTM has no /timeseries endpoint)" },
      { type: "ctm-calls-by-source-bar", label: "CTM Calls by Source", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 3, description: "Per-source call volume — attribution breakdown" },
      { type: "ctm-calls-by-source-answer-rate-stacked", label: "CTM Calls × Source × Answer Rate", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 3, description: "Source quality cross-tab — exposes paid-channel waste" },
      { type: "ctm-calls-list-table", label: "CTM Calls List", icon: Table, defaultColSpan: 12, defaultRowSpan: 4, description: "Per-call records — duration / source / dial_status / sale value / AI likelihood" },
      { type: "ctm-conversation-intelligence-likelihood-quality-pie", label: "CTM AI Likelihood Quality", icon: ChartPieSlice, defaultColSpan: 6, defaultRowSpan: 3, description: "AI lead-quality distribution — 4 quality bands from likelihood score" },
      { type: "ctm-cross-endpoint-scorecard", label: "CTM Cross-Endpoint Scorecard", icon: PhoneCall, defaultColSpan: 12, defaultRowSpan: 1, description: "C-suite scorecard: Total Calls + Answer Rate + AI Likelihood Avg + Conversion Rate" },
      { type: "ctm-omnichannel-touch-funnel", label: "CTM Omnichannel Touch Funnel", icon: Funnel, defaultColSpan: 12, defaultRowSpan: 3, description: "Calls + Forms + SMS unified touch breakdown — the build-to-sell omnichannel widget" },
    ],
  },
  {
    // Story 23.2 — opened Reviews & Reputation. Google Reviews items are
    // the first set. Story 23.4 — Trustpilot items added (Epic 23
    // Trustpilot slice closure). Story 23.6 — Yelp items added (closes
    // Epic 23 Yelp slice + closes Epic 23 in full); Reviews & Reputation
    // section now contains all 3 platforms (Google Reviews + Trustpilot
    // + Yelp).
    // Build-to-sell category-defining widgets:
    //   - `google-reviews-reply-moderation-state-pie` (preset #7 — captures
    //     Google's 2026-04-01 ReviewReplyState API additive)
    //   - `trustpilot-content-integrity-flag-pie` (Story 23.4 preset #7 —
    //     captures Trustpilot's private-tier complianceLabels distribution)
    //   - `yelp-rating-trend-over-time` (Story 23.6 preset #5 — exploits
    //     Story 23.5's D1 `yelp_rating_snapshots` workaround which makes
    //     a Rating × time line chart possible despite Yelp Fusion having
    //     no historical API. NO competitor reporting tool surfaces this).
    //     No competitor reporting tool (AgencyAnalytics + Trustindex +
    //     Databox + Whatagraph + DashThis + Swydo + Supermetrics May 2026
    //     verified 2026-05-18) surfaces these slices.
    id: "reviews",
    label: "Reviews & Reputation",
    icon: Star,
    enabled: true,
    items: [
      { type: "google-reviews-overview-kpi", label: "Reviews Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "4 KPIs: Total Reviews, Average Rating, Reply Rate, Reviews in Period" },
      { type: "google-reviews-rating-distribution-bar", label: "Rating Distribution", icon: ChartBar, defaultColSpan: 6, defaultRowSpan: 3, description: "5-bar chart: 1★ to 5★ review count breakdown" },
      { type: "google-reviews-sentiment-pie", label: "Sentiment Distribution", icon: ChartPieSlice, defaultColSpan: 6, defaultRowSpan: 3, description: "3-wedge pie: Positive (4-5★) / Neutral (3★) / Negative (1-2★) — rule-based (Google does not provide sentiment)" },
      { type: "google-reviews-reply-rate-gauge", label: "Reply Rate Gauge", icon: GaugeIcon, defaultColSpan: 6, defaultRowSpan: 2, description: "Reply Rate 0-100% — APPROVED replies / total reviews (legal-vertical benchmark 70-90%)" },
      { type: "google-reviews-reviews-over-time", label: "Reviews Over Time", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "Daily review volume trend — client-side bucketed (Google v4 has no time-series endpoint)" },
      { type: "google-reviews-reviews-list-table", label: "Reviews List", icon: Table, defaultColSpan: 12, defaultRowSpan: 4, description: "Per-review records: reviewer / star rating / sentiment / comment / reply" },
      { type: "google-reviews-reply-moderation-state-pie", label: "Reply Moderation State", icon: ChartPieSlice, defaultColSpan: 6, defaultRowSpan: 3, description: "3-wedge: APPROVED / PENDING / REJECTED — Google's 2026-04-01 ReviewReplyState additive. Build-to-sell differentiator." },
      { type: "google-reviews-reputation-scorecard", label: "Reputation Scorecard", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "C-suite scorecard: Total Reviews + Average Rating + Reply Rate + Reputation Trend" },
      // Story 23.4 — Trustpilot items (closes Epic 23 Trustpilot slice).
      // Round 2 review fix — labels are prefixed with "Trustpilot " to
      // disambiguate from same-named Google Reviews items in the SAME
      // palette section (prevents "two items labeled 'Reviews List'" UX
      // confusion when a user opens the Reviews & Reputation category).
      { type: "trustpilot-overview-kpi", label: "Trustpilot Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "4 KPIs: TrustScore, Stars, Total Reviews, Verified %" },
      { type: "trustpilot-rating-distribution-bar", label: "Trustpilot Rating Distribution", icon: ChartBar, defaultColSpan: 6, defaultRowSpan: 3, description: "5-bar chart: 5★ to 1★ review count breakdown (Trustpilot brand top-to-bottom)" },
      { type: "trustpilot-sentiment-pie", label: "Trustpilot Sentiment Distribution", icon: ChartPieSlice, defaultColSpan: 6, defaultRowSpan: 3, description: "3-wedge pie: Positive (4-5★) / Neutral (3★) / Negative (1-2★) — rule-based (Trustpilot does not provide sentiment field)" },
      { type: "trustpilot-reply-rate-gauge", label: "Trustpilot Reply Rate Gauge", icon: GaugeIcon, defaultColSpan: 6, defaultRowSpan: 2, description: "Company Reply Rate 0-100% — DTC benchmark 70-80% per Opensend 2026" },
      { type: "trustpilot-reviews-over-time", label: "Trustpilot Reviews Over Time", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "Daily review volume trend — client-side bucketed from review createdAt (Trustpilot v1 has no time-series endpoint)" },
      { type: "trustpilot-reviews-list-table", label: "Trustpilot Reviews List", icon: Table, defaultColSpan: 12, defaultRowSpan: 4, description: "Per-review records: consumer / location / stars / sentiment / verified / text / reply" },
      { type: "trustpilot-content-integrity-flag-pie", label: "Trustpilot Content Integrity Flags", icon: ChartPieSlice, defaultColSpan: 6, defaultRowSpan: 3, description: "4-6 wedge: None / Reported-Pending / Defamation / Obscenity / Hate-Speech / Threats — private-tier complianceLabels array. Build-to-sell differentiator: no competitor reporting tool surfaces this." },
      { type: "trustpilot-reputation-scorecard", label: "Trustpilot Reputation Scorecard", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "C-suite scorecard: TrustScore + Total Reviews + Verified % + TrustScore-Eligible % (Trustpilot-specific wedge)" },
      // Story 23.6 — Yelp items (closes Epic 23 Yelp slice + closes Epic 23
      // in full). Labels prefixed with "Yelp " to disambiguate from
      // same-named Google Reviews + Trustpilot items in the SAME palette
      // section. Build-to-sell category-defining widget = preset #5
      // (`yelp-rating-trend-over-time` — exploits Story 23.5's D1
      // `yelp_rating_snapshots` workaround that no competitor surfaces).
      { type: "yelp-overview-kpi", label: "Yelp Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "4 KPIs: Business Rating, Total Reviews, Price Tier, Listing Status" },
      { type: "yelp-rating-distribution-bar", label: "Yelp Rating Distribution", icon: ChartBar, defaultColSpan: 6, defaultRowSpan: 3, description: "5-bar chart: 5★ to 1★ — Based on 3 most recent excerpts (Yelp Fusion API limitation)" },
      { type: "yelp-sentiment-pie", label: "Yelp Sentiment Distribution", icon: ChartPieSlice, defaultColSpan: 6, defaultRowSpan: 3, description: "3-wedge pie: Positive (4-5★) / Neutral (3★) / Negative (1-2★) — over 3-excerpt subset" },
      { type: "yelp-review-count-growth-line", label: "Yelp Review Count Growth", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "Daily Review Count over 45-day snapshot window — D1 `yelp_rating_snapshots` source" },
      { type: "yelp-rating-trend-over-time", label: "Yelp Rating Trend", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "Daily Business Rating over 45 days — D1 snapshot source. Build-to-sell differentiator: no competitor reporting tool surfaces this." },
      { type: "yelp-tier-coverage-annotation", label: "Yelp Tier Coverage", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "Starter / Plus / Enterprise tier badge + Upgrade CTA when reviewsEndpointEntitled is false" },
      { type: "yelp-review-excerpts-list-table", label: "Yelp Review Excerpts", icon: Table, defaultColSpan: 12, defaultRowSpan: 2, description: "Per-excerpt records: reviewer / rating / time / text / sentiment / Yelp link — 3 rows max (Enhanced cap)" },
      { type: "yelp-reputation-scorecard", label: "Yelp Reputation Scorecard", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "C-suite scorecard: Rating + Reviews + Velocity + Trend Direction (Yelp-specific wedge)" },
      // Story 23.7 — Reviews Cross-Platform items (opens Reviews Cross-Platform
      // slice; cherry-on-top of Epic 23). Labels prefixed with "Cross-Platform "
      // to disambiguate from per-platform items in the SAME section.
      // Build-to-sell category-defining widget = preset #3
      // (`reviews-cross-platform-review-velocity-trend` — joins 3 different
      // review platforms' velocity-over-time signals; no competitor reporting
      // tool surfaces this — verified May 2026).
      // Runtime-gated: these 4 items render ONLY when ALL 3 review platforms
      // (google-reviews + trustpilot + yelp) are connected for the active client.
      { type: "reviews-cross-platform-unified-scoreboard", label: "Cross-Platform Reputation Scoreboard", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 2, description: "3 side-by-side KPIs: Google ★ + Trustpilot ★ + Yelp ★ on the same 0-5 scale — the agency's most-asked reputation question" },
      { type: "reviews-cross-platform-rating-bar-comparison", label: "Cross-Platform Rating Bar Comparison", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 3, description: "3-bar chart on the same 0-5 y-axis — visualizes per-platform reputation divergence (e.g., GreenLife: Trustpilot 4.5 > Yelp 4.4 > Google 4.4)" },
      { type: "reviews-cross-platform-review-velocity-trend", label: "Cross-Platform Review Velocity Trend", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "3-line chart joining Google + Trustpilot + Yelp reviewVelocityPerMonth over 6 months — category-defining differentiator (no competitor reporting tool surfaces this)" },
      { type: "reviews-cross-platform-sentiment-stack", label: "Cross-Platform Sentiment Stack", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 3, description: "Stacked bar chart with 3 platforms on x-axis and positive/neutral/negative bands stacked — reveals per-platform sentiment composition divergence" },
      // Story 23.8 — Reviews Cross-Platform Response Tracking items (closes
      // the Reviews Cross-Platform slice + closes Epic 23 in full). 2-platform
      // scope (Google Reviews + Trustpilot only) — Yelp Fusion ships no reply
      // API surface per Story 23.5 documented constraint. Build-to-sell
      // category-defining widget = preset #3 (`reviews-cross-platform-reply-
      // rate-trend` — joins 2 different review platforms' reply-rate-over-time
      // signals; no competitor reporting tool surfaces this — verified May 2026
      // against AgencyAnalytics + Whatagraph + Databox + Birdeye + Podium +
      // Reputation.com + ReviewTrackers + RightResponse AI).
      // Runtime-gated: these 4 items render ONLY when BOTH Google Reviews AND
      // Trustpilot are connected for the active client (Yelp not required) —
      // separate per-item gate from the 3-platform Story 23.7 items above per
      // Round 4 ADR-A (two declarative sets vs one Map-of-Set).
      { type: "reviews-cross-platform-reply-rate-scoreboard", label: "Cross-Platform Reply Rate Scoreboard", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 2, description: "2 side-by-side KPIs: Google replyRate% + Trustpilot companyReplyRate% — the agency's 'are we responding?' question (Yelp excluded — no reply API)" },
      { type: "reviews-cross-platform-avg-response-time-bar", label: "Cross-Platform Avg Response Time Bar", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 3, description: "2-bar chart showing avg hours between review and reply (Google + Trustpilot) — color-banded by ReplyOnTheFly 2026 benchmarks (<2h excellent / <24h good / <72h fair / 72h+ poor)" },
      { type: "reviews-cross-platform-reply-rate-trend", label: "Cross-Platform Reply Rate Trend (6mo)", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "2-line chart joining Google + Trustpilot rolling reply-rate over 6 months — category-defining differentiator (no competitor reporting tool surfaces this)" },
      { type: "reviews-cross-platform-awaiting-response-queue", label: "Cross-Platform Awaiting Response Queue", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 2, description: "2 KPIs side-by-side: Google + Trustpilot unrepliedReviewCount + oldest-unreplied-days subtitle. Color-banded by oldest age (0-3 normal / 4-7 warn / 8+ alert)" },
    ],
  },
  {
    id: "crm",
    label: "CRM",
    icon: Handshake,
    enabled: true,
    items: [
      { type: "hubspot-crm-pipeline-overview-kpi", label: "Pipeline Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: Total Deals, Pipeline Value, Closed Won Revenue, Win Rate, Avg Days to Close" },
      { type: "hubspot-crm-deal-funnel", label: "Deal Stage Funnel", icon: Funnel, defaultColSpan: 12, defaultRowSpan: 3, description: "Funnel chart showing deals by pipeline stage" },
      { type: "hubspot-crm-lifecycle-funnel", label: "Lifecycle Funnel", icon: Funnel, defaultColSpan: 12, defaultRowSpan: 3, description: "Funnel: Subscriber → Lead → MQL → SQL → Opportunity → Customer" },
      { type: "hubspot-crm-lead-source", label: "Lead Source Breakdown", icon: ChartPieSlice, defaultColSpan: 6, defaultRowSpan: 3, description: "Pie chart: contacts grouped by original source" },
      { type: "hubspot-crm-deals-by-owner", label: "Deals by Owner", icon: ChartBar, defaultColSpan: 6, defaultRowSpan: 3, description: "Bar chart: deal count grouped by owner" },
      { type: "hubspot-crm-revenue-trend", label: "Revenue Trend", icon: TrendUp, defaultColSpan: 6, defaultRowSpan: 2, description: "Line chart: daily closed won revenue over time" },
      { type: "hubspot-crm-deals-table", label: "Deals by Stage", icon: Table, defaultColSpan: 12, defaultRowSpan: 3, description: "Data table: deal stage breakdown with count and pipeline value" },
      // Story 25.5: Attribution presets
      { type: "hubspot-crm-lead-source-detail", label: "Lead Source Detail", icon: GitBranch, defaultColSpan: 12, defaultRowSpan: 4, description: "Data table: contacts by source with channel/network drill-down" },
      { type: "hubspot-crm-first-vs-last-touch", label: "First vs Last Touch", icon: ArrowsLeftRight, defaultColSpan: 12, defaultRowSpan: 3, description: "Side-by-side pie charts: first-touch vs last-touch source comparison" },
      { type: "hubspot-crm-attribution-trend", label: "Attribution Trend", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 3, description: "Line chart: daily new contacts by top 5 sources over time" },
      // Story 25.6: HubSpot email presets (require `content` scope in HubSpot)
      { type: "hubspot-email-overview", label: "Email Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: Sent, Delivered, Open Rate, Click Rate, Unsubscribe Rate" },
      { type: "hubspot-email-campaign-table", label: "Email Campaigns", icon: Table, defaultColSpan: 12, defaultRowSpan: 4, description: "Per-email stats table sorted by sent count" },
      { type: "hubspot-email-device-breakdown", label: "Email Device Breakdown", icon: ChartPieSlice, defaultColSpan: 6, defaultRowSpan: 3, description: "Pie chart: opens by device type (computer / mobile / unknown)" },
      { type: "hubspot-email-funnel", label: "Email Engagement Funnel", icon: Funnel, defaultColSpan: 12, defaultRowSpan: 3, description: "Funnel: Sent → Delivered → Opened → Clicked" },
      // Story 25.7: HubSpot traffic analytics presets (require `content` scope in HubSpot)
      { type: "hubspot-traffic-overview", label: "HubSpot Traffic Overview", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPI widgets: Sessions, Visitors, Page Views, Bounce Rate, Avg Session Duration" },
      { type: "hubspot-traffic-sources", label: "HubSpot Traffic Sources", icon: ChartPieSlice, defaultColSpan: 6, defaultRowSpan: 3, description: "Pie chart: sessions by source type (organic, paid, direct, social, referral, email, other)" },
      { type: "hubspot-traffic-trend", label: "HubSpot Traffic Trend", icon: TrendUp, defaultColSpan: 12, defaultRowSpan: 2, description: "Line chart: daily sessions + visitors over time" },
      // Story 25.8: HubSpot marketing campaign presets (require `content` scope in HubSpot)
      { type: "hubspot-campaign-roi", label: "HubSpot Campaign ROI", icon: Table, defaultColSpan: 12, defaultRowSpan: 4, description: "Data table: per-campaign sessions, contacts, deals, revenue — sorted by revenue desc" },
      { type: "hubspot-campaign-attribution", label: "HubSpot Campaign Attribution", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 3, description: "Bar chart: first-touch vs last-touch new contacts per campaign" },
      // Story 25.9: HubSpot companies, tickets & engagements (new `tickets` scope)
      { type: "hubspot-company-overview", label: "HubSpot Company Overview", icon: Buildings, defaultColSpan: 12, defaultRowSpan: 2, description: "Total companies, new companies, and companies by industry bar chart" },
      { type: "hubspot-ticket-summary", label: "HubSpot Ticket Summary", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 2, description: "4 KPIs: total tickets, open vs closed, avg response time, avg close time" },
      { type: "hubspot-sales-activity", label: "HubSpot Sales Activity", icon: SquaresFour, defaultColSpan: 12, defaultRowSpan: 1, description: "5 KPIs: calls, meetings, CRM emails, notes, completed tasks" },
      // Story 25.10: HubSpot forms + marketing events (optional `forms` + `crm.objects.marketing_events.read` scopes)
      { type: "hubspot-form-performance", label: "HubSpot Form Performance", icon: Table, defaultColSpan: 12, defaultRowSpan: 4, description: "Data table: submissions per form + conversion rate (when views available)" },
      { type: "hubspot-event-attendance", label: "HubSpot Event Attendance", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 3, description: "Bar chart: registrations vs attended per marketing event" },
      // Story 25.11: HubSpot workflows (BETA Automation v4) + deal forecast + geography
      { type: "hubspot-workflow-performance", label: "HubSpot Workflow Performance", icon: Table, defaultColSpan: 12, defaultRowSpan: 4, description: "Data table: workflow enrollments + goal completion rate (last 6 months only — API limit)" },
      { type: "hubspot-contact-geography", label: "HubSpot Contact Geography", icon: ChartBar, defaultColSpan: 12, defaultRowSpan: 3, description: "Bar chart: contacts by country (from contact property)" },
      { type: "hubspot-deal-forecast", label: "HubSpot Deal Forecast", icon: Table, defaultColSpan: 12, defaultRowSpan: 4, description: "Data table: deal stage, forecast probability, amount — sorted by amount" },
    ],
  },
];


// ============================================================================
// Story 20.9 — Runtime-gated palette sections
// ============================================================================

/**
 * Map from palette section id → required connected platforms. A section is
 * HIDDEN unless `connectedPlatforms` is a superset of its required set.
 *
 * Round 4 Red Team Attack #5 defense — the cross-SEO section must NOT
 * render for clients with only 1-2 of the 3 SEO tools connected, because
 * the widgets render empty-state for the missing tools and the comparison
 * is meaningless with fewer than 3 series.
 *
 * Existing per-tool palette sections (Stories 20.3 / 20.6 / 20.8) are
 * unconditionally visible — they're NOT keyed in this map. Future
 * multi-platform-gated sections (e.g. a cross-PPC section) can register
 * new entries here.
 */
export const PALETTE_SECTION_PLATFORM_REQUIREMENTS: Record<
  string,
  readonly string[]
> = {
  "seo-cross-tool": ["ahrefs", "semrush", "moz"],
};

// ============================================================================
// Story 23.7 — Per-ITEM palette gating (Reviews Cross-Platform)
// ============================================================================
//
// DIFFERENT granularity from `PALETTE_SECTION_PLATFORM_REQUIREMENTS` above:
// Story 20.9 created a SEPARATE `id: "seo-cross-tool"` section, so the
// section-level gate was sufficient. Story 23.7 EXTENDS the existing
// `id: "reviews"` section with 4 cross-platform items — a section-level
// gate would hide ALL 28 items (24 per-platform + 4 cross-platform) when
// any of the 3 review platforms is missing. The per-item gate hides ONLY
// the 4 cross-platform items, preserving visibility of the 24 per-platform
// items for clients connecting fewer platforms.
//
// Round 4 ADR-F decision: per-item gate at this granularity is required
// because the section has both gated (cross-platform) and ungated (per-
// platform) items mixed.

/** Set of item types that are subject to per-item integration gating. */
const REVIEWS_CROSS_PLATFORM_ITEM_GATE: ReadonlySet<string> = new Set([
  "reviews-cross-platform-unified-scoreboard",
  "reviews-cross-platform-rating-bar-comparison",
  "reviews-cross-platform-review-velocity-trend",
  "reviews-cross-platform-sentiment-stack",
]);

/** Required platforms for the Reviews Cross-Platform per-item gate. */
const REVIEWS_CROSS_PLATFORM_REQUIRED_PLATFORMS: readonly string[] = [
  "google-reviews",
  "trustpilot",
  "yelp",
];

// ============================================================================
// Story 23.8 — Per-ITEM palette gating (Reviews Cross-Platform Response
// Tracking — 2-platform scope GR + TP only, Yelp excluded per Story 23.5
// documented constraint).
// ============================================================================
//
// SEPARATE from the 3-platform Story 23.7 gate above. Round 4 ADR-A
// decision: TWO declarative sets is more readable than a Map-of-Set
// (gate-set → required-platforms) that would force the consumer to
// branch on per-set requirements at-a-glance.
//
// The 4 Story 23.8 items hide ONLY when the active client lacks EITHER
// Google Reviews OR Trustpilot. Critically, the items REMAIN VISIBLE
// when a client has GR + TP but lacks Yelp — this is the asymmetric-
// visibility property that distinguishes the 23.8 gate from the 23.7
// gate (which requires all 3).

/** Story 23.8 — set of item types subject to the 2-platform per-item gate. */
const REVIEWS_CROSS_PLATFORM_REPLY_ITEM_GATE: ReadonlySet<string> = new Set([
  "reviews-cross-platform-reply-rate-scoreboard",
  "reviews-cross-platform-avg-response-time-bar",
  "reviews-cross-platform-reply-rate-trend",
  "reviews-cross-platform-awaiting-response-queue",
]);

/**
 * Story 23.8 — Required platforms for the Response Tracking per-item gate.
 * 2-platform scope (GR + TP) — Yelp excluded.
 */
const REVIEWS_CROSS_PLATFORM_REPLY_REQUIRED_PLATFORMS: readonly string[] = [
  "google-reviews",
  "trustpilot",
];

/**
 * Filter palette sections by the user's connected-platforms set. Sections
 * with no requirement entry in `PALETTE_SECTION_PLATFORM_REQUIREMENTS`
 * pass through unchanged.
 *
 * Story 20.9 AC #1 + AC #9 — section-level filtering: hide gated sections
 * that demand platforms the user doesn't have.
 *
 * Story 23.7 AC #13 — per-item filtering for the 3-platform reputation
 * widgets: within remaining sections, hide cross-platform items that lack
 * any of the 3 review platforms.
 *
 * Story 23.8 AC #13 — additional per-item filtering for the 2-platform
 * Response Tracking widgets. SEPARATE gate from the 3-platform Story 23.7
 * set — Yelp is NOT required (Yelp Fusion ships no reply surface per
 * Story 23.5). This is the FIRST cross-platform gate in the codebase
 * that intentionally renders fewer platforms than its category supports;
 * the asymmetric visibility (Story 23.7 items hidden but Story 23.8 items
 * shown on a GR + TP client missing Yelp) is the structural property.
 */
export function filterPaletteSectionsByConnectedPlatforms<
  T extends { id: string; items?: readonly { type: string }[] },
>(sections: T[], connectedPlatforms: ReadonlySet<string>): T[] {
  // Step 1 — section-level filtering (Story 20.9 behavior).
  const filteredSections = sections.filter((section) => {
    const required = PALETTE_SECTION_PLATFORM_REQUIREMENTS[section.id];
    if (!required) return true; // Unconditional section — always visible
    return required.every((p) => connectedPlatforms.has(p));
  });

  // Step 2 — per-item filtering within remaining sections. Two parallel
  // gates: the 23.7 3-platform gate AND the 23.8 2-platform gate. Each
  // gate hides ONLY its own items; the 24 per-platform reviews items
  // remain visible regardless of either gate.
  const hasAllReviewsPlatforms =
    REVIEWS_CROSS_PLATFORM_REQUIRED_PLATFORMS.every((p) =>
      connectedPlatforms.has(p),
    );
  const hasReplyTrackingPlatforms =
    REVIEWS_CROSS_PLATFORM_REPLY_REQUIRED_PLATFORMS.every((p) =>
      connectedPlatforms.has(p),
    );

  // Fast path: if BOTH gates are satisfied, no filtering needed.
  if (hasAllReviewsPlatforms && hasReplyTrackingPlatforms) {
    return filteredSections;
  }

  return filteredSections.map((section) => {
    if (!section.items) return section;
    const filteredItems = section.items.filter((item) => {
      // Story 23.7 gate (3-platform requirement).
      if (REVIEWS_CROSS_PLATFORM_ITEM_GATE.has(item.type)) {
        return hasAllReviewsPlatforms;
      }
      // Story 23.8 gate (2-platform requirement — Yelp excluded).
      if (REVIEWS_CROSS_PLATFORM_REPLY_ITEM_GATE.has(item.type)) {
        return hasReplyTrackingPlatforms;
      }
      // Ungated items always pass.
      return true;
    });
    // Only return a new object when filtering actually changed something
    // (cheap optimisation — preserves referential equality for downstream
    // memoisation in the palette).
    if (filteredItems.length === section.items.length) return section;
    return { ...section, items: filteredItems };
  });
}

// ── Backward-compatible export for DragOverlay in generate.tsx ───────

const _metrics = ACTIVE_CATEGORIES.find((c) => c.id === "integrations-metrics")!;
const _content = ACTIVE_CATEGORIES.find((c) => c.id === "content-blocks")!;
const _images = ACTIVE_CATEGORIES.find((c) => c.id === "images")!;
const _embeds = ACTIVE_CATEGORIES.find((c) => c.id === "embeds")!;
const _customMetrics = ACTIVE_CATEGORIES.find((c) => c.id === "custom-metrics")!;
const _benchmarks = ACTIVE_CATEGORIES.find((c) => c.id === "benchmarks")!;
const _goals = ACTIVE_CATEGORIES.find((c) => c.id === "goals")!;

export const WIDGET_PALETTE_ITEMS: PaletteCategory[] = [
  {
    category: "Layout",
    items: [_metrics.items[0]!],
  },
  {
    category: "KPIs",
    items: [_metrics.items[1]!],
  },
  {
    category: "Charts",
    items: _metrics.items.slice(2, 7),
  },
  {
    category: "Social",
    items: [_metrics.items[7]!],
  },
  {
    category: "Content Blocks",
    items: _content.items,
  },
  {
    category: "Content",
    items: _metrics.items.slice(8),
  },
  {
    category: "Images",
    items: _images.items,
  },
  {
    category: "Embeds",
    items: _embeds.items,
  },
  {
    category: "Custom Metrics",
    items: _customMetrics.items,
  },
  {
    category: "Benchmarks",
    items: _benchmarks.items,
  },
  {
    category: "Goals",
    items: _goals.items,
  },
];

// ── Draggable Palette Card (HTML5 native drag) ───────────────────────

/**
 * Shared dnd-kit draggable wiring for a palette card (Epic 37 canvas migration —
 * replaces HTML5 `draggable` + setDragImage). The drag `data` is the typed
 * PaletteDragData the canvas droppable reads; `id` must be unique per card so two
 * cards never share a draggable identity. While dragging, the SOURCE card dims
 * (the floating DragOverlay is the real visual) — a grabbed-state affordance.
 */
function usePaletteDraggable(id: string, data: PaletteDragData) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, data });
  return {
    setNodeRef,
    isDragging,
    dragProps: { ...attributes, ...listeners },
  };
}

function PaletteCard({ item }: { item: PaletteItem }) {
  const Icon = item.icon;
  const { setNodeRef, isDragging, dragProps } = usePaletteDraggable(`palette-${item.type}`, {
    source: "palette",
    type: item.type,
    colSpan: item.defaultColSpan,
    rowSpan: item.defaultRowSpan,
    label: item.label,
  });

  return (
    <div
      ref={setNodeRef}
      {...dragProps}
      role="listitem"
      aria-roledescription="draggable widget"
      aria-label={`Drag ${item.label} onto the canvas`}
      title={item.description}
      data-testid={`palette-card-${item.type}`}
      style={{ opacity: isDragging ? 0.4 : undefined, touchAction: "none" }}
      className="group flex items-center gap-2 p-2 rounded-md border border-border
        bg-background shadow-sm
        hover:border-primary hover:shadow-md transition-all
        cursor-grab active:cursor-grabbing"
    >
      <DotsSixVertical size={14} className="text-foreground shrink-0" />
      <Icon size={18} className="text-muted-foreground shrink-0" />
      <span className="text-sm font-medium truncate">{item.label}</span>
    </div>
  );
}

// ── Platform-scoped Draggable Palette Card ──────────────────────────
// Preview widget is built by the shared `buildPreviewWidget` (widgetPreviewModel)
// — the SAME builder the dnd DragOverlay uses, so the card preview, the drag
// ghost, and the dropped widget can never disagree (Epic 37 dnd-kit migration).

const PlatformPaletteCard = memo(function PlatformPaletteCard({ item, integrationId }: { item: PaletteItem; integrationId: string }) {
  const { setNodeRef, isDragging, dragProps } = usePaletteDraggable(
    `platform-palette-${integrationId}-${item.type}`,
    {
      source: "palette",
      type: item.type,
      colSpan: item.defaultColSpan,
      rowSpan: item.defaultRowSpan,
      integrationId,
      label: item.label,
    },
  );

  // The card preview reflects what a drop produces — same WidgetRenderer, sample
  // data, scaled (ADR-RE-3 / §2.1). Memoized on (item.type, integrationId) so it
  // is not rebuilt on unrelated parent renders (AC #6 / Pre-mortem F).
  const previewWidget = useMemo(
    () => buildPreviewWidget(item, integrationId),
    [item, integrationId],
  );

  return (
    <div
      ref={setNodeRef}
      {...dragProps}
      role="listitem"
      aria-roledescription="draggable widget"
      aria-label={`Drag ${item.label} onto the canvas`}
      title={item.description}
      data-testid={`platform-palette-card-${item.type}`}
      // Outer card border + radius matched to the clients-page table.
      style={{ opacity: isDragging ? 0.4 : undefined, touchAction: "none", border: "0.5px solid var(--hairline)" }}
      onMouseEnter={(e) => { e.currentTarget.style.border = "0.5px solid hsl(214 25% 55%)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.border = "0.5px solid var(--hairline)"; }}
      className="group flex flex-col gap-1.5 p-3 rounded-xl
        bg-background shadow-xs transition-all
        cursor-grab active:cursor-grabbing"
    >
      <div className="flex items-center gap-2">
        <DotsSixVertical size={14} className="text-foreground shrink-0" />
        <span className="text-sm font-medium truncate">{item.label}</span>
      </div>
      {/* Scaled WYSIWYG preview — what this widget looks like on the canvas.
          The scaled WidgetPreview is narrower than the card, so CENTER it in the
          frame (flex justify-center) — otherwise the mini-widget hugs the left
          edge and its centered content reads as "pushed left". */}
      <div
        className="flex justify-center rounded-lg bg-muted/30 overflow-hidden pointer-events-none"
        style={{ border: "0.5px solid var(--hairline)" }}
      >
        <WidgetPreview widget={previewWidget} scale={0.75} frameWidth={216} frameHeight={108} />
      </div>
    </div>
  );
});

// ── Preset Palette Card (with badge + count) ──────────────────────────

/** Badge and count metadata for preset palette cards */
const PRESET_BADGE_META: Record<PresetType, { badge: string; count?: string }> = {
  "email-overview-kpi": { badge: "Mailchimp", count: "5 KPIs" },
  "email-campaign-table": { badge: "Mailchimp" },
  "email-subscriber-growth": { badge: "Mailchimp" },
  "email-top-campaigns": { badge: "Mailchimp" },
  "mailchimp-revenue": { badge: "Mailchimp", count: "3 KPIs" },
  "mailchimp-benchmark": { badge: "Mailchimp", count: "2 Benchmarks" },
  "mailchimp-click-map": { badge: "Mailchimp" },
  "mailchimp-automation": { badge: "Mailchimp" },
  // Story 12.8: Klaviyo-namespaced presets
  "klaviyo-overview-kpi": { badge: "Klaviyo", count: "5 KPIs" },
  "klaviyo-campaign-table": { badge: "Klaviyo" },
  "klaviyo-top-campaigns": { badge: "Klaviyo" },
  "klaviyo-flow-performance": { badge: "Klaviyo" },
  "klaviyo-form-conversion": { badge: "Klaviyo", count: "4 KPIs" },
  "klaviyo-segment-growth": { badge: "Klaviyo" },
  "ecommerce-sales-overview-kpi": { badge: "Shopify", count: "5 KPIs" },
  "ecommerce-revenue-trend": { badge: "Shopify" },
  "ecommerce-top-products": { badge: "Shopify" },
  "ecommerce-orders-conversion": { badge: "Shopify" },
  "ecommerce-product-performance": { badge: "Shopify" },
  "ecommerce-profit": { badge: "Shopify", count: "3 KPIs" },
  "ecommerce-customer": { badge: "Shopify" },
  "ecommerce-cart-abandonment": { badge: "Shopify" },
  "ecommerce-geo": { badge: "Shopify" },
  "instagram-overview-kpi": { badge: "Instagram", count: "5 KPIs" },
  "instagram-reels-table": { badge: "Instagram" },
  "instagram-demographics": { badge: "Instagram" },
  "facebook-pages-overview-kpi": { badge: "Facebook", count: "5 KPIs" },
  "facebook-pages-video": { badge: "Facebook" },
  "facebook-pages-reactions": { badge: "Facebook" },
  "tiktok-ads-overview-kpi": { badge: "TikTok", count: "5 KPIs" },
  "tiktok-ads-video-funnel": { badge: "TikTok" },
  "tiktok-ads-campaign-table": { badge: "TikTok" },
  "tiktok-organic-overview-kpi": { badge: "TikTok", count: "5 KPIs" },
  "tiktok-organic-video-table": { badge: "TikTok" },
  "google-ads-overview-kpi": { badge: "Google Ads", count: "5 KPIs" },
  "google-ads-impression-share": { badge: "Google Ads" },
  "google-ads-keyword-table": { badge: "Google Ads" },
  "google-ads-video-funnel": { badge: "Google Ads" },
  "google-ads-shopping-table": { badge: "Google Ads" },
  "pagespeed-scorecard-kpi": { badge: "PageSpeed", count: "5 KPIs" },
  "pagespeed-core-web-vitals": { badge: "PageSpeed", count: "4 KPIs" },
  "pagespeed-crux-history": { badge: "PageSpeed" },
  "ga4-traffic-overview-kpi": { badge: "GA4", count: "5 KPIs" },
  "ga4-ecommerce-funnel": { badge: "GA4" },
  "ga4-revenue": { badge: "GA4" },
  "ga4-acquisition": { badge: "GA4" },
  "youtube-overview-kpi": { badge: "YouTube", count: "5 KPIs" },
  "youtube-video-table": { badge: "YouTube" },
  "youtube-audience": { badge: "YouTube" },
  "linkedin-pages-overview-kpi": { badge: "LinkedIn Pages", count: "5 KPIs" },
  "linkedin-pages-demographics": { badge: "LinkedIn Pages" },
  "linkedin-ads-overview-kpi": { badge: "LinkedIn Ads", count: "5 KPIs" },
  "linkedin-ads-video-funnel": { badge: "LinkedIn Ads" },
  "linkedin-ads-demographics": { badge: "LinkedIn Ads" },
  "linkedin-ads-lead-gen": { badge: "LinkedIn Ads", count: "4 KPIs" },
  "meta-ads-overview-kpi": { badge: "Meta Ads", count: "5 KPIs" },
  "meta-ads-video-funnel": { badge: "Meta Ads" },
  "meta-ads-conversion-table": { badge: "Meta Ads" },
  "meta-ads-quality-table": { badge: "Meta Ads" },
  // Story 19.3: Microsoft Ads presets
  "microsoft-ads-overview-kpi": { badge: "Microsoft Ads", count: "5 KPIs" },
  "microsoft-ads-impression-share": { badge: "Microsoft Ads", count: "2 Widgets" },
  "microsoft-ads-keyword-quality-table": { badge: "Microsoft Ads", count: "1 Table" },
  "microsoft-ads-campaign-performance": { badge: "Microsoft Ads", count: "1 Table" },
  "microsoft-ads-pmax-final-url-table": { badge: "Microsoft Ads", count: "1 Table" },
  // Story 19.6: Pinterest Ads
  "pinterest-ads-overview-kpi": { badge: "Pinterest Ads", count: "5 KPIs" },
  "pinterest-ads-paid-vs-earned": { badge: "Pinterest Ads", count: "1 Chart" },
  "pinterest-ads-saves-performance": { badge: "Pinterest Ads", count: "3 Widgets" },
  "pinterest-ads-video-funnel": { badge: "Pinterest Ads", count: "1 Funnel" },
  "pinterest-ads-conversion-roas-table": { badge: "Pinterest Ads", count: "1 Table" },
  "pinterest-ads-cross-device-attribution-table": { badge: "Pinterest Ads", count: "1 Table" },
  // Story 19.9: Snapchat Ads
  "snapchat-ads-overview-kpi": { badge: "Snapchat Ads", count: "5 KPIs" },
  "snapchat-ads-swipe-up-funnel": { badge: "Snapchat Ads", count: "1 Funnel" },
  "snapchat-ads-video-quartile-funnel": { badge: "Snapchat Ads", count: "1 Funnel" },
  "snapchat-ads-app-vs-web-conversions": { badge: "Snapchat Ads", count: "2 Widgets" },
  "snapchat-ads-demographic-breakdown": { badge: "Snapchat Ads", count: "1 Widget" },
  "snapchat-ads-conversion-roas-table": { badge: "Snapchat Ads", count: "1 Table" },
  // Story 19.12: Reddit Ads
  "reddit-ads-overview-kpi": { badge: "Reddit Ads", count: "5 KPIs" },
  "reddit-ads-subreddit-performance": { badge: "Reddit Ads", count: "1 Table" },
  "reddit-ads-conversion-attribution-funnel": { badge: "Reddit Ads", count: "1 Funnel" },
  "reddit-ads-video-completion-funnel": { badge: "Reddit Ads", count: "1 Funnel" },
  "reddit-ads-geo-country-region": { badge: "Reddit Ads", count: "1 Widget" },
  "reddit-ads-click-vs-view-attribution": { badge: "Reddit Ads", count: "2 Widgets" },
  // Story 19.17: Amazon Ads (closes Epic 19)
  "amazon-ads-overview-kpi": { badge: "Amazon Ads", count: "5 KPIs" },
  "amazon-ads-advertised-product-performance": { badge: "Amazon Ads", count: "1 Table" },
  "amazon-ads-sb-ntb-funnel": { badge: "Amazon Ads", count: "1 Funnel" },
  "amazon-ads-sd-click-vs-view": { badge: "Amazon Ads", count: "1 Chart" },
  "amazon-ads-dsp-audience-performance": { badge: "Amazon Ads", count: "1 Table" },
  "amazon-ads-dsp-brand-store-engagement-funnel": { badge: "Amazon Ads", count: "1 Funnel" },
  "amazon-ads-acos-roas-trend": { badge: "Amazon Ads", count: "1 Chart" },
  "amazon-ads-cross-surface-comparison": { badge: "Amazon Ads", count: "1 Chart" },
  // Story 20.3: Ahrefs presets (closes Epic 20 Ahrefs slice)
  "ahrefs-domain-rating-gauge": { badge: "Ahrefs", count: "1 Gauge" },
  "ahrefs-backlink-profile-overview": { badge: "Ahrefs", count: "4 KPIs" },
  "ahrefs-organic-traffic-value-trend": { badge: "Ahrefs", count: "1 Chart" },
  "ahrefs-keyword-position-distribution": { badge: "Ahrefs", count: "1 Chart" },
  "ahrefs-site-audit-health-score": { badge: "Ahrefs", count: "1 Gauge" },
  "ahrefs-ai-visibility-sov": { badge: "Ahrefs", count: "1 Gauge" },
  "ahrefs-web-analytics-overview": { badge: "Ahrefs", count: "4 KPIs" },
  "ahrefs-cross-endpoint-kpi-bundle": { badge: "Ahrefs", count: "4 KPIs" },
  // Story 20.6: Semrush presets (closes Epic 20 Semrush slice)
  "semrush-authority-score-gauge": { badge: "Semrush", count: "1 Gauge" },
  "semrush-backlinks-profile-overview": { badge: "Semrush", count: "4 KPIs" },
  "semrush-organic-traffic-cost-trend": { badge: "Semrush", count: "1 Chart" },
  "semrush-position-changes-distribution": { badge: "Semrush", count: "1 Chart" },
  "semrush-top-organic-keywords-table": { badge: "Semrush", count: "1 Table" },
  "semrush-top-organic-competitors-table": { badge: "Semrush", count: "1 Table" },
  "semrush-ai-visibility-mixed": { badge: "Semrush", count: "4 KPIs" },
  "semrush-cross-endpoint-kpi-bundle": { badge: "Semrush", count: "4 KPIs" },
  // Story 20.8: Moz presets (closes Epic 20 Moz slice)
  "moz-domain-authority-gauge": { badge: "Moz", count: "1 Gauge" },
  "moz-page-authority-gauge": { badge: "Moz", count: "1 Gauge" },
  "moz-brand-authority-gauge": { badge: "Moz", count: "1 Gauge" },
  "moz-backlink-profile-overview": { badge: "Moz", count: "4 KPIs" },
  "moz-top-pages-table": { badge: "Moz", count: "1 Table" },
  "moz-top-linking-root-domains-table": { badge: "Moz", count: "1 Table" },
  "moz-da-trend": { badge: "Moz", count: "1 Chart" },
  "moz-cross-endpoint-scorecard": { badge: "Moz", count: "4 KPIs" },
  // Story 22.2 — CallRail presets (closes Epic 22 CallRail slice)
  "callrail-call-volume-kpi": { badge: "CallRail", count: "4 KPIs" },
  "callrail-answer-rate-gauge": { badge: "CallRail", count: "1 Gauge" },
  "callrail-calls-over-time": { badge: "CallRail", count: "1 Chart" },
  "callrail-calls-by-source-bar": { badge: "CallRail", count: "1 Chart" },
  "callrail-calls-by-source-answer-rate-stacked": { badge: "CallRail", count: "1 Chart" },
  "callrail-calls-list-table": { badge: "CallRail", count: "1 Table" },
  "callrail-conversation-intelligence-sentiment-pie": { badge: "CallRail", count: "1 Pie" },
  "callrail-cross-endpoint-scorecard": { badge: "CallRail", count: "4 KPIs" },
  // Story 22.4 — CTM presets (closes Epic 22 in full)
  "ctm-call-volume-kpi": { badge: "CTM", count: "4 KPIs" },
  "ctm-answer-rate-gauge": { badge: "CTM", count: "1 Gauge" },
  "ctm-calls-over-time": { badge: "CTM", count: "1 Chart" },
  "ctm-calls-by-source-bar": { badge: "CTM", count: "1 Chart" },
  "ctm-calls-by-source-answer-rate-stacked": { badge: "CTM", count: "1 Chart" },
  "ctm-calls-list-table": { badge: "CTM", count: "1 Table" },
  "ctm-conversation-intelligence-likelihood-quality-pie": { badge: "CTM", count: "1 Pie" },
  "ctm-cross-endpoint-scorecard": { badge: "CTM", count: "4 KPIs" },
  "ctm-omnichannel-touch-funnel": { badge: "CTM", count: "1 Chart" },
  // Story 20.9 — SEO Cross-Tool (Epic 20 cherry-on-top)
  "seo-cross-tool-authority-scoreboard": { badge: "SEO Cross-Tool", count: "3 KPIs" },
  "seo-cross-tool-authority-bar-comparison": { badge: "SEO Cross-Tool", count: "1 Chart" },
  "seo-cross-tool-authority-trend": { badge: "SEO Cross-Tool", count: "1 Chart" },
  // Story 23.2 — Google Reviews badge meta
  "google-reviews-overview-kpi": { badge: "Google Reviews", count: "4 KPIs" },
  "google-reviews-rating-distribution-bar": { badge: "Google Reviews", count: "1 Chart" },
  "google-reviews-sentiment-pie": { badge: "Google Reviews", count: "1 Chart" },
  "google-reviews-reply-rate-gauge": { badge: "Google Reviews", count: "1 Gauge" },
  "google-reviews-reviews-over-time": { badge: "Google Reviews", count: "1 Chart" },
  "google-reviews-reviews-list-table": { badge: "Google Reviews", count: "1 Table" },
  "google-reviews-reply-moderation-state-pie": { badge: "Google Reviews", count: "1 Chart" },
  "google-reviews-reputation-scorecard": { badge: "Google Reviews", count: "4 KPIs" },
  // Story 23.4 — Trustpilot badge meta (closes Epic 23 Trustpilot slice)
  "trustpilot-overview-kpi": { badge: "Trustpilot", count: "4 KPIs" },
  "trustpilot-rating-distribution-bar": { badge: "Trustpilot", count: "1 Chart" },
  "trustpilot-sentiment-pie": { badge: "Trustpilot", count: "1 Chart" },
  "trustpilot-reply-rate-gauge": { badge: "Trustpilot", count: "1 Gauge" },
  "trustpilot-reviews-over-time": { badge: "Trustpilot", count: "1 Chart" },
  "trustpilot-reviews-list-table": { badge: "Trustpilot", count: "1 Table" },
  "trustpilot-content-integrity-flag-pie": { badge: "Trustpilot", count: "1 Chart" },
  "trustpilot-reputation-scorecard": { badge: "Trustpilot", count: "4 KPIs" },
  // Story 23.6 — Yelp badge meta (closes Epic 23 Yelp slice + closes Epic 23 in full)
  "yelp-overview-kpi": { badge: "Yelp", count: "4 KPIs" },
  "yelp-rating-distribution-bar": { badge: "Yelp", count: "1 Chart" },
  "yelp-sentiment-pie": { badge: "Yelp", count: "1 Chart" },
  "yelp-review-count-growth-line": { badge: "Yelp", count: "1 Chart" },
  "yelp-rating-trend-over-time": { badge: "Yelp", count: "1 Chart" },
  "yelp-tier-coverage-annotation": { badge: "Yelp", count: "1 Card" },
  "yelp-review-excerpts-list-table": { badge: "Yelp", count: "1 Table" },
  "yelp-reputation-scorecard": { badge: "Yelp", count: "4 KPIs" },
  // Story 23.7 — Reviews Cross-Platform badge meta (Epic 23 cherry-on-top;
  // opens Reviews Cross-Platform slice). Mirrors Story 20.9's SEO
  // Cross-Tool badge convention.
  "reviews-cross-platform-unified-scoreboard": { badge: "Reviews Cross-Platform", count: "3 KPIs" },
  "reviews-cross-platform-rating-bar-comparison": { badge: "Reviews Cross-Platform", count: "1 Chart" },
  "reviews-cross-platform-review-velocity-trend": { badge: "Reviews Cross-Platform", count: "1 Chart" },
  "reviews-cross-platform-sentiment-stack": { badge: "Reviews Cross-Platform", count: "1 Chart" },
  // Story 23.8 — Reviews Cross-Platform Response Tracking badge meta
  // (closes Epic 23 + closes Reviews Cross-Platform slice). 2-platform
  // scope (GR + TP only — Yelp excluded per Story 23.5 API constraint).
  "reviews-cross-platform-reply-rate-scoreboard": { badge: "Reviews Cross-Platform", count: "2 KPIs" },
  "reviews-cross-platform-avg-response-time-bar": { badge: "Reviews Cross-Platform", count: "1 Chart" },
  "reviews-cross-platform-reply-rate-trend": { badge: "Reviews Cross-Platform", count: "1 Chart" },
  "reviews-cross-platform-awaiting-response-queue": { badge: "Reviews Cross-Platform", count: "2 KPIs" },
  // Story 25.4: HubSpot CRM presets
  "hubspot-crm-pipeline-overview-kpi": { badge: "HubSpot", count: "5 KPIs" },
  "hubspot-crm-deal-funnel": { badge: "HubSpot", count: "1 Funnel" },
  "hubspot-crm-lifecycle-funnel": { badge: "HubSpot", count: "1 Funnel" },
  "hubspot-crm-lead-source": { badge: "HubSpot" },
  "hubspot-crm-deals-by-owner": { badge: "HubSpot" },
  "hubspot-crm-revenue-trend": { badge: "HubSpot" },
  "hubspot-crm-deals-table": { badge: "HubSpot" },
  // Story 25.5: Attribution presets
  "hubspot-crm-lead-source-detail": { badge: "HubSpot", count: "1 Table" },
  "hubspot-crm-first-vs-last-touch": { badge: "HubSpot", count: "2 Charts" },
  "hubspot-crm-attribution-trend": { badge: "HubSpot", count: "1 Chart" },
  // Story 25.6: HubSpot email presets
  "hubspot-email-overview": { badge: "HubSpot Email", count: "5 KPIs" },
  "hubspot-email-campaign-table": { badge: "HubSpot Email", count: "1 Table" },
  "hubspot-email-device-breakdown": { badge: "HubSpot Email", count: "1 Chart" },
  "hubspot-email-funnel": { badge: "HubSpot Email", count: "1 Funnel" },
  // Story 25.7: HubSpot traffic analytics presets
  "hubspot-traffic-overview": { badge: "HubSpot Traffic", count: "5 KPIs" },
  "hubspot-traffic-sources": { badge: "HubSpot Traffic", count: "1 Chart" },
  "hubspot-traffic-trend": { badge: "HubSpot Traffic", count: "1 Chart" },
  // Story 25.8: HubSpot marketing campaign presets
  "hubspot-campaign-roi": { badge: "HubSpot Campaigns", count: "1 Table" },
  "hubspot-campaign-attribution": { badge: "HubSpot Campaigns", count: "1 Chart" },
  // Story 25.9: HubSpot companies/tickets/engagements presets
  "hubspot-company-overview": { badge: "HubSpot CRM", count: "3 Widgets" },
  "hubspot-ticket-summary": { badge: "HubSpot CRM", count: "4 Widgets" },
  "hubspot-sales-activity": { badge: "HubSpot CRM", count: "5 KPIs" },
  // Story 25.10: HubSpot forms + marketing events presets
  "hubspot-form-performance": { badge: "HubSpot Forms", count: "1 Table" },
  "hubspot-event-attendance": { badge: "HubSpot Events", count: "1 Chart" },
  // Story 25.11: HubSpot workflows + deal forecast + geography presets
  "hubspot-workflow-performance": { badge: "HubSpot Workflows", count: "1 Table" },
  "hubspot-contact-geography": { badge: "HubSpot CRM", count: "1 Chart" },
  "hubspot-deal-forecast": { badge: "HubSpot CRM", count: "1 Table" },
  // IMC-20: GBP presets
  "gbp-review-breakdown": { badge: "GBP", count: "1 Chart" },
  "gbp-search-keywords": { badge: "GBP" },
};

function PresetPaletteCard({ item, presetIntegration }: { item: PaletteItem; presetIntegration?: { id: string; type: string } }) {
  const Icon = item.icon;
  const meta = PRESET_BADGE_META[item.type as PresetType];

  // A preset expands into several widgets — label the overlay with its count so
  // it honestly reads as a bundle, not a single widget.
  const bundleLabel = meta?.count ? `${item.label} · ${meta.count}` : item.label;
  const { setNodeRef, isDragging, dragProps } = usePaletteDraggable(
    `preset-palette-${presetIntegration?.id ?? "none"}-${item.type}`,
    {
      source: "palette",
      type: item.type,
      colSpan: item.defaultColSpan,
      rowSpan: item.defaultRowSpan,
      platform: presetIntegration?.type ?? "",
      integrationId: presetIntegration?.id ?? "",
      label: bundleLabel,
    },
  );

  return (
    <div
      ref={setNodeRef}
      {...dragProps}
      role="listitem"
      aria-roledescription="draggable widget preset"
      aria-label={`Drag ${item.label} onto the canvas`}
      title={item.description}
      data-testid={`preset-palette-card-${item.type}`}
      style={{ opacity: isDragging ? 0.4 : undefined, touchAction: "none" }}
      className="group flex flex-col gap-1.5 p-2 rounded-md border border-border
        bg-background shadow-sm
        hover:border-primary hover:shadow-md transition-all
        cursor-grab active:cursor-grabbing"
    >
      <div className="flex items-center gap-2">
        <DotsSixVertical size={14} className="text-foreground shrink-0" />
        <Icon size={18} className="text-muted-foreground shrink-0" />
        <span className="text-sm font-medium truncate">{item.label}</span>
      </div>
      {meta && (
        <div className="flex items-center gap-1.5 pl-6">
          <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{meta.badge}</span>
          {meta.count && <span className="text-[10px] text-muted-foreground">{meta.count}</span>}
        </div>
      )}
    </div>
  );
}

// ── Category Button (matches left Sidebar nav item styling) ──────────

function CategoryButton({
  category,
  isActive,
  onClick,
  collapsed = false,
}: {
  category: CategoryDef;
  isActive: boolean;
  onClick?: () => void;
  collapsed?: boolean;
}) {
  const Icon = category.icon;

  const isEmailCategory = category.id === "email-marketing";
  const isEcommerceCategory = category.id === "ecommerce";
  const disabledTooltip = isEmailCategory ? "Connect Mailchimp to unlock email presets" : isEcommerceCategory ? "Connect Shopify to unlock e-commerce presets" : "Coming Soon";

  if (!category.enabled) {
    return (
      <li className="relative">
        <div
          data-testid={`category-btn-${category.id}`}
          className="relative z-10 block rounded-lg text-sm font-medium text-foreground/30 cursor-not-allowed"
          aria-disabled="true"
          aria-label={`${category.label} — ${disabledTooltip}`}
          title={collapsed ? category.label : disabledTooltip}
        >
          <motion.div
            className="flex items-center py-2"
            animate={{ paddingLeft: collapsed ? 12 : 12, paddingRight: collapsed ? 0 : 12 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <Icon size={19} className="shrink-0" />
            <motion.span
              initial={false}
              animate={{ opacity: collapsed ? 0 : 1, marginLeft: collapsed ? 0 : 8 }}
              transition={{ duration: collapsed ? 0.15 : 0.3, delay: collapsed ? 0 : 0.15, ease: "easeInOut" }}
              className="whitespace-nowrap"
            >
              {category.label}
            </motion.span>
          </motion.div>
        </div>
      </li>
    );
  }

  const [tooltipPos, setTooltipPos] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const showTooltip = useCallback(() => {
    if (!collapsed || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.top + rect.height / 2,
      right: window.innerWidth - rect.left + 8,
    });
  }, [collapsed]);

  const hideTooltip = useCallback(() => setTooltipPos(null), []);

  return (
    <li key={category.id} className="relative cursor-pointer">
      {isActive && (
        <motion.div
          layoutId="widget-category-active"
          className="absolute inset-0 rounded-lg bg-background border border-input shadow-xs pointer-events-none"
          transition={{ type: "spring", stiffness: 350, damping: 30 }}
        />
      )}
      <button
        ref={buttonRef}
        type="button"
        data-testid={`category-btn-${category.id}`}
        aria-pressed={isActive}
        onClick={onClick}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        className="relative z-10 block w-full rounded-lg text-sm font-normal text-foreground hover:bg-background hover:shadow-xs border border-transparent hover:border-input cursor-pointer text-left"
      >
        <motion.div
          className="flex items-center py-2.5 cursor-pointer"
          animate={{ paddingLeft: collapsed ? 11 : 11, paddingRight: collapsed ? 0 : 12 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <Icon size={19} className="shrink-0 text-foreground" />
          <motion.span
            initial={false}
            animate={{ opacity: collapsed ? 0 : 1, marginLeft: collapsed ? 0 : 7 }}
            transition={{ duration: collapsed ? 0.15 : 0.3, delay: collapsed ? 0 : 0.15, ease: "easeInOut" }}
            className="whitespace-nowrap"
          >
            {category.label}
          </motion.span>
        </motion.div>
      </button>
      {/* Tooltip — portaled to body so it's never clipped by overflow */}
      {tooltipPos && createPortal(
        <div
          className="fixed z-[100] pointer-events-none -translate-y-1/2"
          style={{ top: tooltipPos.top, right: tooltipPos.right }}
        >
          {/* Arrow */}
          <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-background border-r border-t border-input rotate-45 z-10" />
          {/* Label */}
          <div className="relative bg-background text-foreground text-[13px] font-medium px-2.5 py-1 rounded-md border border-input shadow-xs whitespace-nowrap">
            {category.label}
          </div>
        </div>,
        document.getElementById("authenticated-root") ?? document.body,
      )}
    </li>
  );
}

// ── WidgetCategorySidebar — full-height right sidebar ─────────────────

export interface WidgetCategorySidebarProps {
  activeCategory: string | null;
  onCategoryChange: (id: string) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Connected integration platform IDs for the selected client (Story 12.2) */
  connectedIntegrations?: string[];
  /**
   * Story 40-6 (AC6) — when true, this is a ROLL-UP report's palette: hide the
   * sidebar categories whose widget types can't aggregate across clients
   * (custom-metrics + benchmarks). The remaining generic categories
   * (Platform Widgets / Page Elements / Images / Embeds / Goals) stay; the
   * Platform-Widgets panel itself swaps the per-platform preset flow for a flat
   * aggregate-only generic-widget list (see RollupGenericWidgetCardsPanel). The
   * single-client palette is byte-identical when this is false/undefined.
   */
  restrictToRollup?: boolean;
}

export function WidgetCategorySidebar({ activeCategory, onCategoryChange, collapsed = false, onToggleCollapse, connectedIntegrations, restrictToRollup = false }: WidgetCategorySidebarProps) {
  // Story 20.9 Round 1 Code Review C1 fix — runtime gate is now wired into
  // the render path. The cross-SEO palette section is HIDDEN entirely unless
  // all 3 SEO tools are connected (per Round 4 Red Team Attack #5 defense
  // documented at PALETTE_SECTION_PLATFORM_REQUIREMENTS).
  const connectedSet = connectedIntegrations
    ? new Set<string>(connectedIntegrations)
    : new Set<string>();
  // Generic (non-integration-specific) categories that belong in the right
  // sidebar. Per-integration categories (Instagram, Meta Ads, etc.) are
  // hidden from the sidebar — their preset widgets remain reachable via
  // "Platform Widgets" → IntegrationPicker → pick platform, and via Cmd+K.
  const SIDEBAR_CATEGORY_IDS = new Set<string>([
    "integrations-metrics",
    "content-blocks",
    "images",
    "embeds",
    "custom-metrics",
    "benchmarks",
    "goals",
  ]);
  // Story 40-6 (AC6): a roll-up palette drops the categories whose ONLY items
  // are non-aggregatable (`custom-metric`, `benchmark` — both excluded from
  // ROLLUP_ALLOWED_WIDGET_TYPES). The remaining generic categories all have ≥1
  // allowed item, so they stay; the integrations-metrics panel itself narrows to
  // the allowed generic widgets (no presets) inside FloatingCategoryPicker.
  const ROLLUP_HIDDEN_SIDEBAR_IDS = new Set<string>(["custom-metrics", "benchmarks"]);
  const visibleCategories = filterPaletteSectionsByConnectedPlatforms(
    ACTIVE_CATEGORIES,
    connectedSet,
  ).filter(
    (c) =>
      SIDEBAR_CATEGORY_IDS.has(c.id) &&
      !(restrictToRollup && ROLLUP_HIDDEN_SIDEBAR_IDS.has(c.id)),
  );
  // Story 37.2: report-theme is now rendered through the SAME CategoryButton
  // list as the other sidebar icons (no separate <ul>, no divider) — it is
  // appended AFTER the connection filter so it always renders, regardless of
  // which integrations are connected (Pre-mortem Failure B). Its floating
  // panel is owned by CATEGORY_PICKER_REGISTRY["report-theme"] (the registry
  // and this sidebar entry are kept in lockstep by categoryPickerRegistry's
  // tests, which assert report-theme is registered). report-theme is NOT in
  // ACTIVE_CATEGORIES (it has no palette items — its panel is the
  // ReportThemePanel placeholder), so its CategoryDef is defined inline here.
  const reportThemeCategory: CategoryDef = {
    id: "report-theme",
    label: "Report Theme",
    icon: PaletteIcon,
    enabled: true,
  };
  const SIDEBAR_CATEGORIES: CategoryDef[] = [
    ...visibleCategories,
    reportThemeCategory,
  ];
  return (
    <div className="flex flex-col h-full">
      {/* Header — "Widgets" label (minimal height so the icons sit near the top) */}
      <div className="shrink-0 px-4 pt-2 pb-0.5">
        <span
          className="text-[11px] font-semibold tracking-wider text-foreground/70 uppercase"
          style={{ opacity: collapsed ? 0 : 1, transition: "opacity 0.2s ease" }}
        >
          Widgets
        </span>
      </div>

      {/* Nav — exact copy of Sidebar.tsx <motion.nav> */}
      <motion.nav
        data-testid="category-sidebar"
        animate={{ paddingTop: collapsed ? 4 : 2 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="flex-1 px-4 overflow-y-auto [&_div[role=button]]:!cursor-pointer [&_li]:!cursor-pointer [&_div[role=button]_*]:!cursor-pointer"
        aria-label="Widget categories"
      >
        <div className="mb-4">
          {/* Story 37.2: report-theme is no longer a special-cased button in
              its own <ul> — it's appended to the same rendered list as a
              registry-driven category (CATEGORY_PICKER_REGISTRY owns the 8
              floating categories incl. report-theme). It is appended AFTER the
              connection filter so it always renders, regardless of which
              integrations are connected (Pre-mortem Failure B). No divider. */}
          {/* All sidebar categories are unconditionally enabled: the
              integration-gated ids (email-marketing / ecommerce / crm) are
              excluded from SIDEBAR_CATEGORY_IDS, so they never reach this list,
              and report-theme is always enabled. No per-category enable gate
              needed here (Story 37.2 — removed the previously-dead ternary). */}
          <ul className="space-y-1.5">
            {SIDEBAR_CATEGORIES.map((cat) => (
              <CategoryButton
                key={cat.id}
                category={cat}
                isActive={activeCategory === cat.id}
                onClick={() => onCategoryChange(cat.id)}
                collapsed={collapsed}
              />
            ))}
          </ul>
        </div>
      </motion.nav>

      {/* Collapse Toggle — exact copy of Sidebar.tsx collapse button */}
      {onToggleCollapse && (
        <div className="shrink-0 px-4 pb-3">
          <button
            onClick={onToggleCollapse}
            className="flex items-center w-full rounded-lg text-foreground hover:bg-accent/50 transition-colors cursor-pointer"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-testid="collapse-right-sidebar"
          >
            <motion.div
              className="flex items-center py-3 cursor-pointer"
              animate={{ paddingLeft: collapsed ? 12 : 12, paddingRight: collapsed ? 0 : 12 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              {collapsed ? (
                <ArrowLineLeft size={18} className="shrink-0 text-foreground" />
              ) : (
                <ArrowLineRight size={18} className="shrink-0 text-foreground" />
              )}
              <motion.span
                initial={false}
                animate={{ opacity: collapsed ? 0 : 1, marginLeft: collapsed ? 0 : 8 }}
                transition={{ duration: collapsed ? 0.15 : 0.3, delay: collapsed ? 0 : 0.15, ease: "easeInOut" }}
                className="whitespace-nowrap text-sm font-medium"
              >
                Collapse
              </motion.span>
            </motion.div>
          </button>
        </div>
      )}
    </div>
  );
}

// ── Integration Picker — "Choose your Metrics" sub-panel ─────────────

// All 30 integrations for the picker (14 active + 16 coming soon)
const ALL_PICKER_PLATFORMS = PICKER_PLATFORMS;

export function IntegrationPickerPanel({
  selectedIntegration,
  onSelect,
}: {
  selectedIntegration: string | null;
  onSelect: (platform: string | null) => void;
}) {
  const [search, setSearch] = useState("");
  const filtered = search
    ? ALL_PICKER_PLATFORMS.filter((p) =>
        p.displayName.toLowerCase().includes(search.toLowerCase()),
      )
    : ALL_PICKER_PLATFORMS;

  return (
    <div className="flex flex-col h-full">
      {/* Search input — matches Clients page search */}
      <div className="px-4 pt-4 pb-4">
        <div className="relative">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search integrations..."
            className="flex h-11 w-full rounded-2xl bg-background px-3 py-2 pl-9 pr-9 text-[13.5px] text-foreground placeholder:text-foreground shadow-xs focus:outline-none focus-visible:outline-none"
            style={{ border: "0.5px solid var(--hairline)" }}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-foreground/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded cursor-pointer"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Integration list */}
      <ul className="flex-1 overflow-y-auto px-4">
        {filtered.map((platform) => {
          const isSelected = selectedIntegration === platform.platform;
          return (
            <li key={platform.platform}>
              <button
                onClick={() => onSelect(isSelected ? null : platform.platform)}
                className={`flex items-center w-full py-2.5 px-3 rounded-lg cursor-pointer group border-[0.5px] ${
                  isSelected
                    ? "bg-background shadow-xs"
                    : "border-transparent hover:bg-background hover:shadow-xs"
                }`}
                style={isSelected ? { borderColor: "var(--hairline)" } : undefined}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.borderColor = "var(--hairline)"; }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.borderColor = "transparent"; }}
              >
                <img
                  src={logoUrl(platform.domain)}
                  alt=""
                  className="w-5 h-5 shrink-0 rounded-sm"
                />
                <span className="ml-3 mr-3 text-[13.5px] font-normal text-foreground truncate">
                  {platform.displayName}
                </span>
                <CaretRight
                  size={14}
                  className="ml-auto shrink-0 text-foreground"
                />
              </button>
            </li>
          );
        })}
        {filtered.length === 0 && (
          <li className="px-3 py-4 text-sm text-muted-foreground text-center">
            No integrations found
          </li>
        )}
      </ul>
    </div>
  );
}

// ── Platform Widget Cards Panel — shows widget types for a selected platform ──

/**
 * Per-platform widget compatibility map (Integration Mapping Reference directive).
 *
 * Rules applied:
 * - kpi/data-table/goal/benchmark/custom-metric: any platform with ≥1 numeric metric (gauge excluded — no palette card yet)
 * - line-chart: needs Time dimension (date-based time series)
 * - bar-chart/pie-chart: needs ≥1 non-Time dimension
 * - funnel: conversion-focused platforms (ads, email, commerce, CRM)
 * - top-posts: social platforms with post-level content data
 * - demographic: platforms with Age/Gender/Location (Demo) dimensions
 */
const UNIVERSAL_WIDGETS: WidgetType[] = ["kpi", "data-table", "goal", "benchmark", "custom-metric"];

export const PLATFORM_WIDGET_COMPATIBILITY: Record<string, WidgetType[]> = {
  // ── Google ──
  "google-analytics-4": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "funnel", "demographic"],
  "google-search-console": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart"],
  "google-ads": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "funnel"],
  "youtube-analytics": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "demographic", "top-posts"],
  "google-business-profile": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "data-table"],
  "google-pagespeed": [...UNIVERSAL_WIDGETS, "line-chart"],
  // ── Social Media ──
  "facebook-pages": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "demographic", "top-posts"],
  "facebook-meta-ads": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "funnel", "demographic"],
  "instagram-business": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "demographic", "top-posts"],
  "instagram-ads": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "funnel", "demographic"],
  "linkedin-pages": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "demographic"],
  "linkedin-ads": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "funnel", "demographic"],
  "tiktok-business": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "demographic", "top-posts"],
  "twitter-x-organic": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "top-posts"],
  "twitter-x-ads": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "demographic"],
  // ── Advertising ──
  "microsoft-ads": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "funnel"],
  "pinterest-ads": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "demographic", "funnel"],
  "snapchat-ads": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "demographic", "funnel"],
  "reddit-ads": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "demographic", "funnel"],
  "amazon-ads": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "funnel"],
  // ── SEO ──
  "ahrefs": [...UNIVERSAL_WIDGETS, "bar-chart", "pie-chart"],
  "semrush": [...UNIVERSAL_WIDGETS, "bar-chart", "pie-chart"],
  "moz": [...UNIVERSAL_WIDGETS],
  // ── Email & Commerce ──
  "mailchimp": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "funnel"],
  "klaviyo": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "funnel"],
  "shopify": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "funnel"],
  // ── Call Tracking ──
  "callrail": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart"],
  "calltrackingmetrics": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart"],
  // ── Reviews ──
  "google-reviews": [...UNIVERSAL_WIDGETS],
  "trustpilot": [...UNIVERSAL_WIDGETS],
  "yelp": [...UNIVERSAL_WIDGETS],
  // ── CRM ──
  "hubspot": [...UNIVERSAL_WIDGETS, "line-chart", "bar-chart", "pie-chart", "funnel", "demographic"],
};

/** Collect all PaletteItem definitions from all categories for lookup */
const ALL_DATA_WIDGET_ITEMS: PaletteItem[] = (() => {
  const allItems = ACTIVE_CATEGORIES.flatMap((c) => c.items);
  const seen = new Set<string>();
  return allItems.filter((item) => {
    if (seen.has(item.type)) return false;
    seen.add(item.type);
    return true;
  });
})();

/** Get PaletteItems for a specific platform, filtered by compatibility */
function getPlatformWidgetItems(platformId: string): PaletteItem[] {
  const compatible = PLATFORM_WIDGET_COMPATIBILITY[platformId];
  if (!compatible) return ALL_DATA_WIDGET_ITEMS.filter((i) => UNIVERSAL_WIDGETS.includes(i.type as WidgetType));
  const compatibleSet = new Set<string>(compatible);
  return ALL_DATA_WIDGET_ITEMS.filter((item) => compatibleSet.has(item.type));
}

export interface PlatformWidgetCardsPanelProps {
  integrationId: string;
  platformName: string;
  onBack: () => void;
}

export function PlatformWidgetCardsPanel({ integrationId, platformName, onBack }: PlatformWidgetCardsPanelProps) {
  // Story 37.7 AC #6: each card now renders a (scaled) WidgetPreview, so the
  // grid is heavier than the old icon list — memoize the item set on the
  // platform id (it's a pure function of the platform + the static
  // compatibility map) so the grid doesn't recompute/re-mount on unrelated
  // parent renders (§6 cross-cutting cost note / Pre-mortem F). The cards
  // themselves are React.memo'd with stable props.
  const items = useMemo(() => getPlatformWidgetItems(integrationId), [integrationId]);

  // Resolve the integration's logo domain so the header can show its icon.
  const platformDomain = PICKER_PLATFORMS.find((p) => p.platform === integrationId)?.domain;

  return (
    <div data-testid="platform-widget-cards-panel" className="flex flex-col h-full">
      {/* Header: back caret + integration ICON + name. Name uses the report-title
          color (text-foreground, semibold) — not muted — so it reads as a title. */}
      <div className="px-4 pt-3 pb-2">
        <button
          onClick={onBack}
          data-testid="platform-widgets-back"
          className="group flex items-center gap-2 text-foreground transition-colors cursor-pointer"
        >
          <CaretLeft size={16} className="shrink-0 text-foreground" />
          {platformDomain && (
            <img src={logoUrl(platformDomain)} alt="" className="h-5 w-5 shrink-0 rounded-sm" />
          )}
          <span className="truncate text-[14px] font-medium">{platformName}</span>
        </button>
      </div>

      <div className="px-4 pb-2">
        <p className="text-[11px] text-muted-foreground">Drag onto the canvas</p>
      </div>

      {/* Widget cards */}
      <div role="list" className="flex-1 overflow-y-auto px-4 pb-3 flex flex-col gap-3">
        {items.map((item) => (
          <PlatformPaletteCard key={item.type} item={item} integrationId={integrationId} />
        ))}
      </div>
    </div>
  );
}

// ── WidgetCardsPanel — draggable widget cards (fills the floating shell) ──
// Story 37.2: this panel's production render path is now the floating
// FloatingCategoryPicker shell (it fills it via w-full h-full); it is no
// longer rendered inline in the right sidebar.

export interface WidgetCardsPanelProps {
  activeCategory: string;
  /** Full integration objects for resolving integrationId in presets (Story 12.2 review fix) */
  clientIntegrations?: { id: string; type: string }[];
}

/** E-commerce platform types for resolving integration bindings */
const ECOMMERCE_PLATFORMS = new Set(["shopify"]);

/** Shared / Mailchimp email preset types (hidden when only Klaviyo is connected per Story 12.8 AC #11) */
const MAILCHIMP_ONLY_PRESETS = new Set<string>([
  "email-overview-kpi",
  "email-campaign-table",
  "email-subscriber-growth",
  "email-top-campaigns",
  "mailchimp-revenue",
  "mailchimp-benchmark",
  "mailchimp-click-map",
  "mailchimp-automation",
]);

/** Klaviyo-namespaced preset types (Story 12.8) — hidden unless Klaviyo is connected */
const KLAVIYO_PRESETS = new Set<string>([
  "klaviyo-overview-kpi",
  "klaviyo-campaign-table",
  "klaviyo-top-campaigns",
  "klaviyo-flow-performance",
  "klaviyo-form-conversion",
  "klaviyo-segment-growth",
]);

export function WidgetCardsPanel({ activeCategory, clientIntegrations }: WidgetCardsPanelProps) {
  const activeCat = ACTIVE_CATEGORIES.find((c) => c.id === activeCategory);
  const items = activeCat?.items ?? [];
  const [cardsSearch, setCardsSearch] = useState("");

  // Resolve the first connected email integration for preset binding.
  // Prefer Klaviyo for klaviyo-* presets and Mailchimp for shared/mailchimp-* presets.
  const mailchimpIntegration = clientIntegrations?.find((i) => i.type === "mailchimp");
  const klaviyoIntegration = clientIntegrations?.find((i) => i.type === "klaviyo");
  const emailIntegration = mailchimpIntegration ?? klaviyoIntegration;

  // Resolve the first connected e-commerce integration for preset binding
  const ecommerceIntegration = clientIntegrations?.find((i) => ECOMMERCE_PLATFORMS.has(i.type));

  // Resolve the first connected CRM integration for preset binding (Story 25.4)
  const hubspotIntegration = clientIntegrations?.find((i) => i.type === "hubspot");

  // Story 20.9 Round 1 Code Review C4 fix — cross-SEO presets need an
  // Ahrefs integration ID to bind to. The Semrush + Moz IDs are resolved
  // at render time via the `resolve:<platform>` sentinel pattern (see
  // widgetPresets.ts cross-tool section). Cross-SEO category is hidden
  // entirely unless all 3 SEO tools are connected (per the category-sidebar
  // runtime gate), so reaching this code path implies all 3 are connected.
  const ahrefsIntegration = clientIntegrations?.find((i) => i.type === "ahrefs");
  const semrushIntegration = clientIntegrations?.find((i) => i.type === "semrush");
  const mozIntegration = clientIntegrations?.find((i) => i.type === "moz");
  const seoCrossToolReady = Boolean(
    ahrefsIntegration && semrushIntegration && mozIntegration,
  );

  // Story 23.7 Round 3 Code Review Edge Case Hunter Finding R3-EH-02 fix —
  // bind the Google Reviews integration for the 4 reviews-cross-platform
  // presets. Without this binding, Preset #1's Google KPI was dragged
  // with `integrationId: ""` and emitted a fail-loud envelope even on
  // the GreenLife demo workspace where the integration IS connected.
  // Mirrors Story 20.9's Ahrefs binding pattern (the primary slot for
  // SEO Cross-Tool); Trustpilot + Yelp remain `resolve:` sentinels
  // resolved at widget-populate time per AC #16.
  const googleReviewsIntegration = clientIntegrations?.find(
    (i) => i.type === "google-reviews",
  );

  // Gate: if this is the email-marketing category but no email integration is connected, show nothing
  const isEmailCategory = activeCategory === "email-marketing";
  if (isEmailCategory && !emailIntegration) {
    return (
      <div
        data-testid="widget-cards-panel"
        className="w-full h-full p-3 overflow-y-auto"
      >
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          {activeCat?.label ?? "Widgets"}
        </h3>
        <p className="text-[11px] text-muted-foreground">Connect Mailchimp or Klaviyo to unlock email presets</p>
      </div>
    );
  }

  // Gate: if this is the ecommerce category but no Shopify integration is connected, show nothing
  const isEcommerceCategory = activeCategory === "ecommerce";
  if (isEcommerceCategory && !ecommerceIntegration) {
    return (
      <div
        data-testid="widget-cards-panel"
        className="w-full h-full p-3 overflow-y-auto"
      >
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          {activeCat?.label ?? "Widgets"}
        </h3>
        <p className="text-[11px] text-muted-foreground">Connect Shopify to unlock e-commerce presets</p>
      </div>
    );
  }

  // Gate: if this is the CRM category but no HubSpot integration is connected, show nothing (Story 25.4)
  const isCrmCategory = activeCategory === "crm";
  if (isCrmCategory && !hubspotIntegration) {
    return (
      <div
        data-testid="widget-cards-panel"
        className="w-full h-full p-3 overflow-y-auto"
      >
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          {activeCat?.label ?? "Widgets"}
        </h3>
        <p className="text-[11px] text-muted-foreground">Connect HubSpot to unlock CRM presets</p>
      </div>
    );
  }

  // Story 20.9 Round 1 Code Review C1 fix — gate: cross-SEO requires all 3
  // SEO tools connected. The category-sidebar gate above should already
  // hide this category entirely, but defense-in-depth: if a caller renders
  // the card panel directly with the cross-SEO category active and tools
  // missing, show the connect-prompt empty state.
  const isSeoCrossToolCategory = activeCategory === "seo-cross-tool";
  if (isSeoCrossToolCategory && !seoCrossToolReady) {
    const missing = [
      !ahrefsIntegration ? "Ahrefs" : null,
      !semrushIntegration ? "Semrush" : null,
      !mozIntegration ? "Moz" : null,
    ].filter((v): v is string => v !== null);
    return (
      <div
        data-testid="widget-cards-panel"
        className="w-full h-full p-3 overflow-y-auto"
      >
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          {activeCat?.label ?? "Widgets"}
        </h3>
        <p className="text-[11px] text-muted-foreground">
          Connect {missing.join(" + ")} to unlock cross-tool SEO presets
        </p>
      </div>
    );
  }

  // Story 12.8: Filter email preset items based on which platforms are connected.
  // - Klaviyo-only tenant → hide shared/Mailchimp presets (keeps palette uncluttered)
  // - Mailchimp-only tenant → hide Klaviyo presets
  // - Both connected → show all
  const integrationFiltered = isEmailCategory
    ? items.filter((item) => {
        if (KLAVIYO_PRESETS.has(item.type)) {
          return Boolean(klaviyoIntegration);
        }
        if (MAILCHIMP_ONLY_PRESETS.has(item.type)) {
          return Boolean(mailchimpIntegration);
        }
        return true;
      })
    : items;

  const cardsQuery = cardsSearch.trim().toLowerCase();
  const filteredItems = cardsQuery
    ? integrationFiltered.filter((it) => it.label.toLowerCase().includes(cardsQuery) || it.type.toLowerCase().includes(cardsQuery))
    : integrationFiltered;

  return (
    <div
      data-testid="widget-cards-panel"
      className="w-full h-full p-3 overflow-y-auto"
    >
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {activeCat?.label ?? "Widgets"}
      </h3>
      {/* Search within category — matches Clients page search style */}
      <div className="relative mb-3">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground/60 pointer-events-none" />
        <input
          type="text"
          value={cardsSearch}
          onChange={(e) => setCardsSearch(e.target.value)}
          placeholder="Search widgets..."
          data-testid="widget-cards-search"
          className="flex h-8 w-full rounded-lg bg-background pl-8 pr-7 text-[12px] text-foreground placeholder:text-foreground/40 shadow-xs focus:outline-none"
          style={{ border: "0.5px solid var(--hairline)" }}
        />
        {cardsSearch && (
          <button
            type="button"
            onClick={() => setCardsSearch("")}
            aria-label="Clear search"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-foreground/40 hover:text-foreground rounded cursor-pointer"
          >
            <X size={12} />
          </button>
        )}
      </div>
      {filteredItems.length === 0 ? (
        <p className="text-[11px] text-muted-foreground py-4 text-center">No widgets match</p>
      ) : (
      <div role="list" className="flex flex-col gap-1.5">
        {filteredItems.map((item) => {
          // Story 12.8: Klaviyo presets bind to Klaviyo integration; shared email presets bind to Mailchimp (if present, else Klaviyo fallback)
          // Story 20.9 Round 1 Code Review C4 fix — cross-SEO presets bind
          // to the Ahrefs integrationId (the Semrush + Moz IDs resolve at
          // render-time via sentinels).
          // Story 23.7 Round 3 fix — Reviews Cross-Platform presets bind
          // to the Google Reviews integration as the primary slot (the
          // Trustpilot + Yelp sentinels resolve at widget-populate time
          // via the demo-only resolver). Detected by item.type prefix
          // because the 4 cross-platform items live in the `reviews`
          // section alongside 24 per-platform items — a section-level
          // category check would over-bind.
          const isReviewsCrossPlatformItem = item.type.startsWith(
            "reviews-cross-platform-",
          );
          const itemPresetIntegration = isEmailCategory
            ? (KLAVIYO_PRESETS.has(item.type) ? klaviyoIntegration : (mailchimpIntegration ?? klaviyoIntegration))
            : isEcommerceCategory ? ecommerceIntegration
            : isCrmCategory ? hubspotIntegration
            : isSeoCrossToolCategory ? ahrefsIntegration
            : isReviewsCrossPlatformItem ? googleReviewsIntegration
            : undefined;
          return isPresetType(item.type)
            ? <PresetPaletteCard key={item.type} item={item} presetIntegration={itemPresetIntegration} />
            : <PaletteCard key={item.type} item={item} />;
        })}
      </div>
      )}
    </div>
  );
}

// ── Roll-up generic widget cards (Story 40-6, AC6) ───────────────────
// The Platform-Widgets panel for a ROLL-UP report. A roll-up has NO single
// integration to drill into (its members span many clients, each with its own
// integrations), so the per-platform IntegrationPicker → PlatformWidgetCards
// flow is meaningless and is suppressed. Instead this shows a FLAT list of the
// aggregate-only GENERIC widget cards (ROLLUP_ALLOWED_WIDGET_TYPES) — the same
// PaletteCard the single-client generic path uses, so drag/drop is identical.
// Presets are NEVER shown (they bind a single integrationId).

/** The de-duplicated GENERIC palette items (no presets) whose base type
 *  aggregates in a roll-up — collected once from ACTIVE_CATEGORIES so the set
 *  stays in lockstep with the live palette definitions (e.g. a future allowed
 *  type added to a category is picked up automatically). Cover Page leads (it's
 *  the canonical first widget), matching the single-client Platform-Widgets
 *  panel's ordering. */
export const ROLLUP_GENERIC_PALETTE_ITEMS: PaletteItem[] = (() => {
  const seen = new Set<string>();
  const collected: PaletteItem[] = [];
  for (const cat of ACTIVE_CATEGORIES) {
    for (const item of cat.items) {
      if (isPresetType(item.type)) continue; // never offer a preset in a roll-up
      if (!isRollupAllowedWidgetType(item.type)) continue;
      if (seen.has(item.type)) continue;
      seen.add(item.type);
      collected.push(item);
    }
  }
  return collected;
})();

export function RollupGenericWidgetCardsPanel() {
  const [cardsSearch, setCardsSearch] = useState("");
  const cardsQuery = cardsSearch.trim().toLowerCase();
  const filteredItems = cardsQuery
    ? ROLLUP_GENERIC_PALETTE_ITEMS.filter(
        (it) =>
          it.label.toLowerCase().includes(cardsQuery) ||
          it.type.toLowerCase().includes(cardsQuery),
      )
    : ROLLUP_GENERIC_PALETTE_ITEMS;

  return (
    <div
      data-testid="rollup-widget-cards-panel"
      className="w-full h-full p-3 overflow-y-auto"
    >
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        Widgets
      </h3>
      <p className="text-[11px] text-muted-foreground mb-3">
        Roll-up reports aggregate one metric across every client — only
        cross-client widgets are available.
      </p>
      <div className="relative mb-3">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground/60 pointer-events-none" />
        <input
          type="text"
          value={cardsSearch}
          onChange={(e) => setCardsSearch(e.target.value)}
          placeholder="Search widgets..."
          data-testid="rollup-widget-cards-search"
          className="flex h-8 w-full rounded-lg bg-background pl-8 pr-7 text-[12px] text-foreground placeholder:text-foreground/40 shadow-xs focus:outline-none"
          style={{ border: "0.5px solid var(--hairline)" }}
        />
        {cardsSearch && (
          <button
            type="button"
            onClick={() => setCardsSearch("")}
            aria-label="Clear search"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-foreground/40 hover:text-foreground rounded cursor-pointer"
          >
            <X size={12} />
          </button>
        )}
      </div>
      {filteredItems.length === 0 ? (
        <p className="text-[11px] text-muted-foreground py-4 text-center">No widgets match</p>
      ) : (
        <div role="list" className="flex flex-col gap-1.5">
          {filteredItems.map((item) => (
            <PaletteCard key={item.type} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── WidgetPalette — combined component (backward compat) ─────────────

export function WidgetPalette() {
  const [activeCategory, setActiveCategory] = useState(ACTIVE_CATEGORIES[0]!.id);

  return (
    <div
      data-testid="widget-palette"
      className="flex shrink-0 overflow-hidden"
      aria-label="Widget palette"
    >
      <div className="shrink-0 bg-background" style={{ width: 180 }}>
        <WidgetCategorySidebar
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />
      </div>
      {/* Story 37.2: WidgetCardsPanel now fills its container (w-full h-full)
          because its only production consumer is the FloatingCategoryPicker
          shell, which supplies the bg/border/radius/size. This combined
          WidgetPalette is backward-compat only (no production JSX usage), so it
          must supply its own sized shell for the panel to render correctly. The
          250 standardization (PICKER_PANEL_WIDTH) applies to the FLOATING shell;
          this legacy inline scaffold keeps the historical 220px inline width on
          purpose (importing PICKER_PANEL_WIDTH here would create a circular
          import, since the registry imports this module's panels). */}
      <div
        className="shrink-0 border-l border-border bg-muted/30 overflow-y-auto"
        style={{ width: 220, height: "100%" }}
      >
        <WidgetCardsPanel activeCategory={activeCategory} />
      </div>
    </div>
  );
}
